import { getContract, pad, encodePacked, parseUnits, formatUnits } from 'viem';
import axios from 'axios';

const CLIENT_TIME_OUT = 60 * 1e3;
const ESTIMATE_AMOUNT_DATA_RELOAD = 3e4;
const EXPLORER_URL = {
  cBridge: "https://celerscan.com/tx/",
  deBridge: "https://app.debridge.finance/orders?s="
};
const VALIDATION_API_TIMEOUT = 10 * 1e3;
const EVM_NATIVE_TOKEN_ADDRESS = "0x0000000000000000000000000000000000000000";
const SOLANA_NATIVE_TOKEN_ADDRESS = "11111111111111111111111111111111";
const TRON_CHAIN_ID = 728126428;

function isSameAddress(A, B) {
  if (!A || !B) return false;
  if (isEvmAddress(A) && isEvmAddress(B)) {
    return A.toLowerCase() === B.toLowerCase();
  }
  return A === B;
}
function isEvmAddress(address) {
  return !!address && /^0x[a-f0-9]{40}$/i.test(address);
}
function isNativeToken(tokenAddress, chainType = "evm") {
  if (chainType === "solana") {
    return tokenAddress === SOLANA_NATIVE_TOKEN_ADDRESS;
  }
  return tokenAddress === EVM_NATIVE_TOKEN_ADDRESS;
}
function isSolanaAddress(address) {
  return !!address && /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
}
function isTronAddress(address) {
  return !!address && /^T[a-zA-Z0-9]{33}$/.test(address);
}
const isValidTokenAddress = ({
  contractAddress,
  chainType,
  isSourceChain
}) => {
  const fromOrTo = isSourceChain ? "from" : "to";
  if (chainType === "evm") {
    if (!isEvmAddress(contractAddress)) {
      console.log(
        `Invalid evm ${fromOrTo} contract address`,
        chainType,
        contractAddress
      );
      return false;
    }
  } else if (chainType === "solana") {
    if (!isSolanaAddress(contractAddress)) {
      console.log(
        `Invalid solana ${fromOrTo} contract address`,
        chainType,
        contractAddress
      );
      return false;
    }
  } else {
    console.log(`Invalid ${fromOrTo} chain type`, chainType, contractAddress);
    return false;
  }
  return true;
};

function isEmpty(obj) {
  return !obj || Object.entries(obj).length === 0;
}
function uniqueArr(arr) {
  const map = /* @__PURE__ */ new Map();
  return arr.filter((item) => {
    if (map.get(item)) {
      return false;
    }
    map.set(item, true);
    return true;
  });
}

class BaseAdapter {
  options;
  config;
  excludedChains = [];
  excludedTokens = {};
  assetPrefix = "";
  nativeCurrencies = {};
  brandChains = [];
  externalChains = [];
  displayTokenSymbols = {};
  chainConfigs = [];
  chains = [];
  chainMap = /* @__PURE__ */ new Map();
  tokenMap = /* @__PURE__ */ new Map();
  symbolMap = /* @__PURE__ */ new Map();
  transferMap = /* @__PURE__ */ new Map();
  constructor(options) {
    this.options = options;
    this.config = options.config ?? {};
    this.excludedChains = options.excludedChains ?? [];
    this.excludedTokens = options.excludedTokens ?? {};
    this.assetPrefix = options?.assetPrefix ?? "";
    this.brandChains = options?.brandChains ?? [];
    this.externalChains = options?.externalChains ?? [];
    this.displayTokenSymbols = options?.displayTokenSymbols ?? {};
    this.chainConfigs = options?.chainConfigs ?? [];
    this.nativeCurrencies = options?.nativeCurrencies ?? {};
  }
  init() {
    this.initChains();
    this.initTokens();
    this.initTransferMap();
    this.filterTransferMap();
    return this;
  }
  filterTransferMap() {
    if (!this.brandChains.length) {
      return;
    }
    const filteredTransferMap = /* @__PURE__ */ new Map();
    this.transferMap.forEach((toMap, fromChainId) => {
      if (this.brandChains.includes(fromChainId)) {
        filteredTransferMap.set(fromChainId, toMap);
      } else {
        toMap.forEach((tokenPairMap, toChainId) => {
          if (this.brandChains.includes(toChainId)) {
            if (!filteredTransferMap.has(fromChainId)) {
              filteredTransferMap.set(
                fromChainId,
                /* @__PURE__ */ new Map()
              );
            }
            filteredTransferMap.get(fromChainId)?.set(toChainId, tokenPairMap);
          }
        });
      }
    });
    this.transferMap = filteredTransferMap;
  }
  getChainBaseInfo(chainId) {
    const chainConfig = this.chainConfigs.find((e) => e.id === chainId);
    const explorerConfig = chainConfig?.blockExplorers?.default ?? {};
    const explorerUrl = explorerConfig?.url?.replace(/\/$/, "") ?? "";
    const tmpUrlPattern = explorerUrl ? `${explorerUrl}/token/{0}` : "";
    const tokenUrlPattern = explorerConfig?.tokenUrlPattern || tmpUrlPattern;
    const externalConfig = this.externalChains?.find(
      (item) => item.chainId === chainId
    );
    const chainType = externalConfig ? "link" : chainConfig?.chainType ?? "evm";
    const externalBridgeUrl = externalConfig?.bridgeUrl;
    return {
      id: chainId,
      name: chainConfig?.name ?? "",
      icon: `${this.assetPrefix}/images/chains/${chainId}.png?v=${1736947457518}`,
      explorerUrl,
      tokenUrlPattern,
      chainType,
      externalBridgeUrl
    };
  }
  getTokenDisplaySymbolAndIcon({
    chainId,
    defaultSymbol,
    tokenAddress,
    icon
  }) {
    const symbolMap = this.displayTokenSymbols[chainId] ?? {};
    const target = Object.entries(symbolMap).find(
      ([address]) => isSameAddress(address, tokenAddress)
    );
    const displaySymbol = target?.[1] ?? defaultSymbol;
    const iconSymbol = displaySymbol?.toUpperCase();
    const commonTokenIcon = `${this.assetPrefix}/images/tokens/${iconSymbol}.png?v=${1736947457518}`;
    return {
      displaySymbol,
      icon: icon || commonTokenIcon
    };
  }
  getToTokensForPair({
    fromChainId,
    toChainId,
    fromTokenSymbol
  }) {
    const fromNativeSymbol = this.nativeCurrencies[fromChainId].symbol.toUpperCase();
    const toNativeSymbol = this.nativeCurrencies[toChainId].symbol.toUpperCase();
    const tokenMap = this.symbolMap.get(toChainId);
    const toTokens = [...tokenMap?.get(fromTokenSymbol) ?? []];
    if (["ETH", "WETH"].includes(fromTokenSymbol) && (fromNativeSymbol === "ETH" || toNativeSymbol === "ETH") && this.id === "deBridge") {
      const ethTokens = tokenMap?.get("ETH") ?? [];
      const wethTokens = tokenMap?.get("WETH") ?? [];
      toTokens.push(...ethTokens);
      toTokens.push(...wethTokens);
    }
    const bridgedGroup = this.bridgedTokenGroups.find(
      (e) => e.includes(fromTokenSymbol)
    );
    bridgedGroup?.forEach((anotherTokenSymbol) => {
      const anotherToTokens = tokenMap?.get(anotherTokenSymbol.toUpperCase());
      if (anotherToTokens?.length) {
        toTokens.push(...anotherToTokens);
      }
    });
    return uniqueArr(toTokens);
  }
  getTokenSymbolByTokenAddress({
    chainId,
    tokenAddress
  }) {
    const tokens = this.tokenMap.get(chainId);
    let baseInfo;
    const token = tokens?.find((t) => {
      baseInfo = this.getTokenBaseInfo({
        chainId,
        token: t
      });
      return isSameAddress(baseInfo.address, tokenAddress);
    });
    if (token) {
      return baseInfo?.symbol?.toUpperCase();
    }
  }
  getChainById(chainId) {
    return this.chainMap.get(chainId);
  }
  getFromChains() {
    const fromChainIds = new Set(this.transferMap.keys());
    const fromChains = this.chains.filter(
      (chain) => fromChainIds.has(this.getChainId(chain))
    );
    return fromChains;
  }
  getToChains({ fromChainId }) {
    const toChainIds = new Set(this.transferMap.get(fromChainId)?.keys());
    const toChains = this.chains.filter(
      (chain) => toChainIds.has(this.getChainId(chain))
    );
    return toChains;
  }
  getAllTokenPairsOnFromChain({
    fromChainId,
    toChainId
  }) {
    const all = /* @__PURE__ */ new Map();
    this.transferMap.get(fromChainId)?.forEach((toMap) => {
      toMap.forEach((tokenPairs, tokenAddress) => {
        all.set(tokenAddress, tokenPairs);
      });
    });
    this.transferMap.get(fromChainId)?.get(toChainId)?.forEach((tokenPairs, tokenAddress) => {
      all.set(tokenAddress, tokenPairs);
    });
    return Array.from(all.values());
  }
  getTokenPairs({
    fromChainId,
    toChainId,
    tokenAddress
  }) {
    return this.transferMap.get(fromChainId)?.get(toChainId)?.get(tokenAddress?.toLowerCase());
  }
  getTokenPair({
    fromChainId,
    toChainId,
    fromTokenAddress,
    toTokenAddress
  }) {
    const tokenPairs = this.getTokenPairs({
      fromChainId,
      toChainId,
      tokenAddress: fromTokenAddress
    });
    return tokenPairs?.find(
      (e) => isSameAddress(e.toTokenAddress, toTokenAddress)
    );
  }
  isExcludedToken({
    chainId,
    tokenSymbol,
    tokenAddress
  }) {
    const excludedTokens = this.excludedTokens[chainId] ?? [];
    return excludedTokens?.some(
      (e) => e.toUpperCase() === tokenSymbol.toUpperCase() || isSameAddress(e, tokenAddress)
    );
  }
  isToChainCompatible({
    fromChainId,
    toChainId
  }) {
    return !!this.transferMap.get(fromChainId)?.get(toChainId);
  }
  isTokenCompatible(params) {
    return !!this.getTokenPairs(params);
  }
}

class CBridgeAdapter extends BaseAdapter {
  id = "cBridge";
  bridgedTokenGroups = [];
  peggedPairConfigs = [];
  burnPairConfigs = [];
  init() {
    this.initChains();
    this.initTokens();
    this.initPeggedPairConfigs();
    this.initBurnPairConfigs();
    this.initTransferMap();
    this.filterTransferMap();
    return this;
  }
  initChains() {
    const { chains, chain_token, pegged_pair_configs } = this.config;
    const filteredChains = chains.filter((chain) => {
      const hasChainConfig = !!this.chainConfigs.find((e) => e.id === chain.id);
      const isExcludedChain = this.excludedChains.includes(chain.id);
      const hasEnabledToken = chain_token[chain.id]?.token?.some(
        (e) => !e.token.xfer_disabled
      );
      const hasPeggedToken = pegged_pair_configs.some(
        (e) => (e.org_chain_id === chain.id || e.pegged_chain_id === chain.id) && !e.org_token.token.xfer_disabled && !e.pegged_token.token.xfer_disabled
      );
      return hasChainConfig && !isExcludedChain && (hasEnabledToken || hasPeggedToken);
    });
    const chainMap = /* @__PURE__ */ new Map();
    filteredChains.forEach((chain) => {
      chainMap.set(chain.id, chain);
    });
    this.chains = filteredChains;
    this.chainMap = chainMap;
  }
  initTokens() {
    const { chain_token } = this.config;
    const tokenMap = /* @__PURE__ */ new Map();
    const symbolMap = /* @__PURE__ */ new Map();
    Object.entries(chain_token).forEach(([id, { token: chainTokens }]) => {
      const chainId = Number(id);
      const filteredTokens = chainTokens.filter((token) => {
        const isEnabledToken = !token.token.xfer_disabled;
        const isExcludedToken = this.isExcludedToken({
          chainId,
          tokenSymbol: token.token.symbol?.toUpperCase(),
          tokenAddress: token.token.address
        });
        return isEnabledToken && !isExcludedToken;
      });
      if (filteredTokens.length > 0 && this.chainMap.has(chainId)) {
        symbolMap.set(chainId, /* @__PURE__ */ new Map());
        filteredTokens.forEach((token) => {
          const tokenSymbol = token.token.symbol?.toUpperCase();
          if (!symbolMap.get(chainId)?.get(tokenSymbol)) {
            symbolMap.get(chainId)?.set(tokenSymbol, []);
          }
          symbolMap.get(chainId)?.get(tokenSymbol)?.push(token);
        });
        tokenMap.set(chainId, filteredTokens);
      }
    });
    this.tokenMap = tokenMap;
    this.symbolMap = symbolMap;
  }
  initTransferMap() {
    const transferMap = /* @__PURE__ */ new Map();
    this.chains.forEach((fromChain) => {
      this.chains.forEach((toChain) => {
        if (fromChain.id !== toChain.id) {
          const fromTokens = this.tokenMap.get(fromChain.id) ?? [];
          const tokenPairsMap = /* @__PURE__ */ new Map();
          fromTokens.forEach((fromToken) => {
            const fromTokenSymbol = fromToken.token.symbol?.toUpperCase();
            const isBlocked = this.isBlockedPair({
              fromChainId: fromChain.id,
              toChainId: toChain.id,
              tokenSymbol: fromTokenSymbol
            });
            if (isBlocked) {
              return;
            }
            const toTokens = this.getToTokensForPair({
              fromChainId: fromChain.id,
              toChainId: toChain.id,
              fromTokenSymbol
            });
            if (toTokens?.length) {
              const tokenPairs = [];
              toTokens.forEach((toToken) => {
                tokenPairs.push({
                  fromChainId: fromChain.id,
                  toChainId: toChain.id,
                  fromTokenAddress: fromToken.token.address,
                  toTokenAddress: toToken.token.address,
                  fromToken,
                  toToken
                });
              });
              tokenPairsMap.set(
                fromToken.token.address?.toLowerCase(),
                tokenPairs
              );
            }
          });
          if (tokenPairsMap.size > 0) {
            if (!transferMap.has(fromChain.id)) {
              transferMap.set(
                fromChain.id,
                /* @__PURE__ */ new Map()
              );
            }
            transferMap.get(fromChain.id)?.set(toChain.id, tokenPairsMap);
          }
        }
      });
    });
    const addPeggedTokenPair = (fromChainId, fromToken, toChainId, toToken, item) => {
      const isBlocked = this.isBlockedPair({
        fromChainId,
        toChainId,
        tokenSymbol: fromToken.token.symbol
      });
      if (isBlocked) {
        return;
      }
      const addressKey = fromToken.token.address?.toLowerCase();
      if (!transferMap.get(fromChainId)?.get(toChainId)?.get(addressKey)) {
        const peggedTokenPair = {
          fromChainId,
          toChainId,
          fromTokenAddress: fromToken.token.address,
          toTokenAddress: toToken.token.address,
          fromToken,
          toToken,
          isPegged: true,
          peggedConfig: item
        };
        if (!transferMap.has(fromChainId)) {
          transferMap.set(
            fromChainId,
            /* @__PURE__ */ new Map()
          );
        }
        if (!transferMap.get(fromChainId)?.get(toChainId)) {
          transferMap.get(fromChainId)?.set(toChainId, /* @__PURE__ */ new Map());
        }
        if (!transferMap.get(fromChainId)?.get(toChainId)?.get(addressKey)) {
          transferMap.get(fromChainId)?.get(toChainId)?.set(addressKey, []);
        }
        transferMap.get(fromChainId)?.get(toChainId)?.get(addressKey)?.push(peggedTokenPair);
      }
    };
    this.peggedPairConfigs.forEach((item) => {
      const fromChainId = item.org_chain_id;
      const fromToken = item.org_token;
      const toChainId = item.pegged_chain_id;
      const toToken = item.pegged_token;
      addPeggedTokenPair(fromChainId, fromToken, toChainId, toToken, item);
      addPeggedTokenPair(toChainId, toToken, fromChainId, fromToken, item);
    });
    this.transferMap = transferMap;
  }
  initPeggedPairConfigs() {
    const peggedPairConfigs = this.config.pegged_pair_configs;
    const isAvailablePair = (chainId, token) => {
      const hasChain = this.chainMap.has(chainId);
      const isEnabledToken = !token.token.xfer_disabled;
      const isExcludedToken = this.isExcludedToken({
        chainId,
        tokenSymbol: token.token.symbol,
        tokenAddress: token.token.address
      });
      return hasChain && isEnabledToken && !isExcludedToken;
    };
    const filteredPeggedPairConfigs = peggedPairConfigs.filter(
      (item) => isAvailablePair(item.org_chain_id, item.org_token) && isAvailablePair(item.pegged_chain_id, item.pegged_token)
    );
    this.peggedPairConfigs = filteredPeggedPairConfigs;
  }
  initBurnPairConfigs() {
    const burnPairConfigs = [];
    for (let i = 0; i < this.peggedPairConfigs.length; i++) {
      for (let j = i + 1; j < this.peggedPairConfigs.length; j++) {
        const A = this.peggedPairConfigs[i];
        const B = this.peggedPairConfigs[j];
        if (A.org_chain_id === B.org_chain_id && A.org_token.token.symbol === B.org_token.token.symbol) {
          if (A.bridge_version === 2 && B.bridge_version === 2) {
            burnPairConfigs.push({
              burn_config_as_org: {
                chain_id: A.pegged_chain_id,
                token: A.pegged_token,
                burn_contract_addr: A.pegged_burn_contract_addr,
                canonical_token_contract_addr: A.canonical_token_contract_addr,
                burn_contract_version: A.bridge_version
              },
              burn_config_as_dst: {
                chain_id: B.pegged_chain_id,
                token: B.pegged_token,
                burn_contract_addr: B.pegged_burn_contract_addr,
                canonical_token_contract_addr: B.canonical_token_contract_addr,
                burn_contract_version: B.bridge_version
              }
            });
            burnPairConfigs.push({
              burn_config_as_org: {
                chain_id: B.pegged_chain_id,
                token: B.pegged_token,
                burn_contract_addr: B.pegged_burn_contract_addr,
                canonical_token_contract_addr: B.canonical_token_contract_addr,
                burn_contract_version: B.bridge_version
              },
              burn_config_as_dst: {
                chain_id: A.pegged_chain_id,
                token: A.pegged_token,
                burn_contract_addr: A.pegged_burn_contract_addr,
                canonical_token_contract_addr: A.canonical_token_contract_addr,
                burn_contract_version: A.bridge_version
              }
            });
          }
        }
      }
    }
    this.burnPairConfigs = burnPairConfigs;
  }
  getChainId(chain) {
    return chain.id;
  }
  getTokenBaseInfo({
    chainId,
    token
  }) {
    return {
      name: token.name,
      symbol: token.token.symbol,
      address: token.token.address,
      decimals: token.token.decimal,
      ...this.getTokenDisplaySymbolAndIcon({
        chainId,
        tokenAddress: token.token.address,
        defaultSymbol: token.token.symbol,
        icon: token.icon
      })
    };
  }
  getPeggedPairConfigs() {
    return this.burnPairConfigs;
  }
  getBurnPairConfigs() {
    return this.peggedPairConfigs;
  }
  isBlockedPair({
    fromChainId,
    toChainId,
    tokenSymbol
  }) {
    const blockedList = this.config.blocked_bridge_direct ?? [];
    const isBlocked = blockedList.find((e) => {
      return Number(e.src_chain_id) === fromChainId && Number(e.dst_chain_id) === toChainId && e.symbol.toUpperCase() === tokenSymbol.toUpperCase();
    });
    return isBlocked;
  }
}

function cBridge(params) {
  return {
    id: "cBridge",
    ...params
  };
}

const POOL_TRANSFER_BRIDGE = [
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "period",
        type: "uint256"
      }
    ],
    name: "DelayPeriodUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "threshold",
        type: "uint256"
      }
    ],
    name: "DelayThresholdUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "id",
        type: "bytes32"
      }
    ],
    name: "DelayedTransferAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "id",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "DelayedTransferExecuted",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "length",
        type: "uint256"
      }
    ],
    name: "EpochLengthUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "cap",
        type: "uint256"
      }
    ],
    name: "EpochVolumeUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint64",
        name: "seqnum",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "address",
        name: "provider",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "LiquidityAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MaxSendUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MinAddUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MinSendUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "transferId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "sender",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "srcChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "srcTransferId",
        type: "bytes32"
      }
    ],
    name: "Relay",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "resetTime",
        type: "uint256"
      }
    ],
    name: "ResetNotification",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "transferId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "sender",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "dstChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "nonce",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "maxSlippage",
        type: "uint32"
      }
    ],
    name: "Send",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address[]",
        name: "_signers",
        type: "address[]"
      },
      {
        indexed: false,
        internalType: "uint256[]",
        name: "_powers",
        type: "uint256[]"
      }
    ],
    name: "SignersUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Unpaused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "withdrawId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "seqnum",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "refid",
        type: "bytes32"
      }
    ],
    name: "WithdrawDone",
    type: "event"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_account",
        type: "address"
      }
    ],
    name: "addGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_token",
        type: "address"
      },
      {
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      }
    ],
    name: "addLiquidity",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      }
    ],
    name: "addNativeLiquidity",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "addPauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "addseq",
    outputs: [
      {
        internalType: "uint64",
        name: "",
        type: "uint64"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "delayPeriod",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "delayThresholds",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "",
        type: "bytes32"
      }
    ],
    name: "delayedTransfers",
    outputs: [
      {
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        internalType: "uint256",
        name: "timestamp",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "epochLength",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "epochVolumeCaps",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "epochVolumes",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "id",
        type: "bytes32"
      }
    ],
    name: "executeDelayedTransfer",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "governors",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "period",
        type: "uint256"
      }
    ],
    name: "increaseNoticePeriod",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_account",
        type: "address"
      }
    ],
    name: "isGovernor",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "isPauser",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "lastOpTimestamps",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "maxSend",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "minAdd",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "minSend",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "minimalMaxSlippage",
    outputs: [
      {
        internalType: "uint32",
        name: "",
        type: "uint32"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "nativeWrap",
    outputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "noticePeriod",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "notifyResetSigners",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "paused",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "pausers",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes",
        name: "_relayRequest",
        type: "bytes"
      },
      {
        internalType: "bytes[]",
        name: "_sigs",
        type: "bytes[]"
      },
      {
        internalType: "address[]",
        name: "_signers",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_powers",
        type: "uint256[]"
      }
    ],
    name: "relay",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_account",
        type: "address"
      }
    ],
    name: "removeGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "removePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renouncePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_signers",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_powers",
        type: "uint256[]"
      }
    ],
    name: "resetSigners",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "resetTime",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_receiver",
        type: "address"
      },
      {
        internalType: "address",
        name: "_token",
        type: "address"
      },
      {
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      },
      {
        internalType: "uint64",
        name: "_dstChainId",
        type: "uint64"
      },
      {
        internalType: "uint64",
        name: "_nonce",
        type: "uint64"
      },
      {
        internalType: "uint32",
        name: "_maxSlippage",
        type: "uint32"
      }
    ],
    name: "send",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_receiver",
        type: "address"
      },
      {
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      },
      {
        internalType: "uint64",
        name: "_dstChainId",
        type: "uint64"
      },
      {
        internalType: "uint64",
        name: "_nonce",
        type: "uint64"
      },
      {
        internalType: "uint32",
        name: "_maxSlippage",
        type: "uint32"
      }
    ],
    name: "sendNative",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "_period",
        type: "uint256"
      }
    ],
    name: "setDelayPeriod",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_thresholds",
        type: "uint256[]"
      }
    ],
    name: "setDelayThresholds",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "_length",
        type: "uint256"
      }
    ],
    name: "setEpochLength",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_caps",
        type: "uint256[]"
      }
    ],
    name: "setEpochVolumeCaps",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_amounts",
        type: "uint256[]"
      }
    ],
    name: "setMaxSend",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_amounts",
        type: "uint256[]"
      }
    ],
    name: "setMinAdd",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_amounts",
        type: "uint256[]"
      }
    ],
    name: "setMinSend",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint32",
        name: "_minimalMaxSlippage",
        type: "uint32"
      }
    ],
    name: "setMinimalMaxSlippage",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_weth",
        type: "address"
      }
    ],
    name: "setWrap",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "ssHash",
    outputs: [
      {
        internalType: "bytes32",
        name: "",
        type: "bytes32"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "",
        type: "bytes32"
      }
    ],
    name: "transfers",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "triggerTime",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "_triggerTime",
        type: "uint256"
      },
      {
        internalType: "address[]",
        name: "_newSigners",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_newPowers",
        type: "uint256[]"
      },
      {
        internalType: "bytes[]",
        name: "_sigs",
        type: "bytes[]"
      },
      {
        internalType: "address[]",
        name: "_curSigners",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_curPowers",
        type: "uint256[]"
      }
    ],
    name: "updateSigners",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes",
        name: "_msg",
        type: "bytes"
      },
      {
        internalType: "bytes[]",
        name: "_sigs",
        type: "bytes[]"
      },
      {
        internalType: "address[]",
        name: "_signers",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_powers",
        type: "uint256[]"
      }
    ],
    name: "verifySigs",
    outputs: [],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes",
        name: "_wdmsg",
        type: "bytes"
      },
      {
        internalType: "bytes[]",
        name: "_sigs",
        type: "bytes[]"
      },
      {
        internalType: "address[]",
        name: "_signers",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_powers",
        type: "uint256[]"
      }
    ],
    name: "withdraw",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "",
        type: "bytes32"
      }
    ],
    name: "withdraws",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    stateMutability: "payable",
    type: "receive"
  }
];

const ORIGINAL_TOKEN_VAULT = [
  {
    inputs: [
      {
        internalType: "contract ISigsVerifier",
        name: "_sigsVerifier",
        type: "address"
      }
    ],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "period",
        type: "uint256"
      }
    ],
    name: "DelayPeriodUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "threshold",
        type: "uint256"
      }
    ],
    name: "DelayThresholdUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "bytes32", name: "id", type: "bytes32" }],
    name: "DelayedTransferAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bytes32", name: "id", type: "bytes32" },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "DelayedTransferExecuted",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "depositId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "mintChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "address",
        name: "mintAccount",
        type: "address"
      }
    ],
    name: "Deposited",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "length",
        type: "uint256"
      }
    ],
    name: "EpochLengthUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      { indexed: false, internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "EpochVolumeUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MaxDepositUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MinDepositUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Unpaused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "withdrawId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "refChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "refId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "burnAccount",
        type: "address"
      }
    ],
    name: "Withdrawn",
    type: "event"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "addGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "addPauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "delayPeriod",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "delayThresholds",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "delayedTransfers",
    outputs: [
      { internalType: "address", name: "receiver", type: "address" },
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "uint256", name: "timestamp", type: "uint256" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint64", name: "_mintChainId", type: "uint64" },
      { internalType: "address", name: "_mintAccount", type: "address" },
      { internalType: "uint64", name: "_nonce", type: "uint64" }
    ],
    name: "deposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint64", name: "_mintChainId", type: "uint64" },
      { internalType: "address", name: "_mintAccount", type: "address" },
      { internalType: "uint64", name: "_nonce", type: "uint64" }
    ],
    name: "depositNative",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [],
    name: "epochLength",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "epochVolumeCaps",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "epochVolumes",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "id", type: "bytes32" }],
    name: "executeDelayedTransfer",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "governors",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "isGovernor",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "isPauser",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "lastOpTimestamps",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "maxDeposit",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "minDeposit",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "nativeWrap",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "paused",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "pausers",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "records",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "removeGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "removePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renouncePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_period", type: "uint256" }],
    name: "setDelayPeriod",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_thresholds", type: "uint256[]" }
    ],
    name: "setDelayThresholds",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_length", type: "uint256" }],
    name: "setEpochLength",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_caps", type: "uint256[]" }
    ],
    name: "setEpochVolumeCaps",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_amounts", type: "uint256[]" }
    ],
    name: "setMaxDeposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_amounts", type: "uint256[]" }
    ],
    name: "setMinDeposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_weth", type: "address" }],
    name: "setWrap",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "sigsVerifier",
    outputs: [{ internalType: "contract ISigsVerifier", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "bytes", name: "_request", type: "bytes" },
      { internalType: "bytes[]", name: "_sigs", type: "bytes[]" },
      { internalType: "address[]", name: "_signers", type: "address[]" },
      { internalType: "uint256[]", name: "_powers", type: "uint256[]" }
    ],
    name: "withdraw",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  { stateMutability: "payable", type: "receive" }
];

const ORIGINAL_TOKEN_VAULT_V2 = [
  {
    inputs: [
      {
        internalType: "contract ISigsVerifier",
        name: "_sigsVerifier",
        type: "address"
      }
    ],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "period",
        type: "uint256"
      }
    ],
    name: "DelayPeriodUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "threshold",
        type: "uint256"
      }
    ],
    name: "DelayThresholdUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "bytes32", name: "id", type: "bytes32" }],
    name: "DelayedTransferAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bytes32", name: "id", type: "bytes32" },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "DelayedTransferExecuted",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "depositId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "mintChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "address",
        name: "mintAccount",
        type: "address"
      },
      { indexed: false, internalType: "uint64", name: "nonce", type: "uint64" }
    ],
    name: "Deposited",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "length",
        type: "uint256"
      }
    ],
    name: "EpochLengthUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      { indexed: false, internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "EpochVolumeUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MaxDepositUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MinDepositUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Unpaused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "withdrawId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "refChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "refId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "burnAccount",
        type: "address"
      }
    ],
    name: "Withdrawn",
    type: "event"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "addGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "addPauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "delayPeriod",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "delayThresholds",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "delayedTransfers",
    outputs: [
      { internalType: "address", name: "receiver", type: "address" },
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "uint256", name: "timestamp", type: "uint256" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint64", name: "_mintChainId", type: "uint64" },
      { internalType: "address", name: "_mintAccount", type: "address" },
      { internalType: "uint64", name: "_nonce", type: "uint64" }
    ],
    name: "deposit",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint64", name: "_mintChainId", type: "uint64" },
      { internalType: "address", name: "_mintAccount", type: "address" },
      { internalType: "uint64", name: "_nonce", type: "uint64" }
    ],
    name: "depositNative",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [],
    name: "epochLength",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "epochVolumeCaps",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "epochVolumes",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "id", type: "bytes32" }],
    name: "executeDelayedTransfer",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "governors",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "isGovernor",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "isPauser",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "lastOpTimestamps",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "maxDeposit",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "minDeposit",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "nativeWrap",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "paused",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "pausers",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "records",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "removeGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "removePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renouncePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_period", type: "uint256" }],
    name: "setDelayPeriod",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_thresholds", type: "uint256[]" }
    ],
    name: "setDelayThresholds",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_length", type: "uint256" }],
    name: "setEpochLength",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_caps", type: "uint256[]" }
    ],
    name: "setEpochVolumeCaps",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_amounts", type: "uint256[]" }
    ],
    name: "setMaxDeposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_amounts", type: "uint256[]" }
    ],
    name: "setMinDeposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_weth", type: "address" }],
    name: "setWrap",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "sigsVerifier",
    outputs: [{ internalType: "contract ISigsVerifier", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "bytes", name: "_request", type: "bytes" },
      { internalType: "bytes[]", name: "_sigs", type: "bytes[]" },
      { internalType: "address[]", name: "_signers", type: "address[]" },
      { internalType: "uint256[]", name: "_powers", type: "uint256[]" }
    ],
    name: "withdraw",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  { stateMutability: "payable", type: "receive" }
];

const PEGGED_TOKEN_BRIDGE = [
  {
    inputs: [
      {
        internalType: "contract ISigsVerifier",
        name: "_sigsVerifier",
        type: "address"
      }
    ],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "burnId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "address",
        name: "withdrawAccount",
        type: "address"
      }
    ],
    name: "Burn",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "period",
        type: "uint256"
      }
    ],
    name: "DelayPeriodUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "threshold",
        type: "uint256"
      }
    ],
    name: "DelayThresholdUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "id",
        type: "bytes32"
      }
    ],
    name: "DelayedTransferAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "id",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "DelayedTransferExecuted",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "length",
        type: "uint256"
      }
    ],
    name: "EpochLengthUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "cap",
        type: "uint256"
      }
    ],
    name: "EpochVolumeUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MaxBurnUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MinBurnUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "mintId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "refChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "refId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "depositor",
        type: "address"
      }
    ],
    name: "Mint",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Unpaused",
    type: "event"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_account",
        type: "address"
      }
    ],
    name: "addGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "addPauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_token",
        type: "address"
      },
      {
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      },
      {
        internalType: "address",
        name: "_withdrawAccount",
        type: "address"
      },
      {
        internalType: "uint64",
        name: "_nonce",
        type: "uint64"
      }
    ],
    name: "burn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "delayPeriod",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "delayThresholds",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "",
        type: "bytes32"
      }
    ],
    name: "delayedTransfers",
    outputs: [
      {
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        internalType: "uint256",
        name: "timestamp",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "epochLength",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "epochVolumeCaps",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "epochVolumes",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "id",
        type: "bytes32"
      }
    ],
    name: "executeDelayedTransfer",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "governors",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_account",
        type: "address"
      }
    ],
    name: "isGovernor",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "isPauser",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "lastOpTimestamps",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "maxBurn",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "minBurn",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes",
        name: "_request",
        type: "bytes"
      },
      {
        internalType: "bytes[]",
        name: "_sigs",
        type: "bytes[]"
      },
      {
        internalType: "address[]",
        name: "_signers",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_powers",
        type: "uint256[]"
      }
    ],
    name: "mint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "paused",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "",
        type: "address"
      }
    ],
    name: "pausers",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "bytes32",
        name: "",
        type: "bytes32"
      }
    ],
    name: "records",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "_account",
        type: "address"
      }
    ],
    name: "removeGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "removePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renouncePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "_period",
        type: "uint256"
      }
    ],
    name: "setDelayPeriod",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_thresholds",
        type: "uint256[]"
      }
    ],
    name: "setDelayThresholds",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "_length",
        type: "uint256"
      }
    ],
    name: "setEpochLength",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_caps",
        type: "uint256[]"
      }
    ],
    name: "setEpochVolumeCaps",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_amounts",
        type: "uint256[]"
      }
    ],
    name: "setMaxBurn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address[]",
        name: "_tokens",
        type: "address[]"
      },
      {
        internalType: "uint256[]",
        name: "_amounts",
        type: "uint256[]"
      }
    ],
    name: "setMinBurn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "sigsVerifier",
    outputs: [
      {
        internalType: "contract ISigsVerifier",
        name: "",
        type: "address"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  }
];

const PEGGED_TOKEN_BRIDGE_V2 = [
  {
    inputs: [
      {
        internalType: "contract ISigsVerifier",
        name: "_sigsVerifier",
        type: "address"
      }
    ],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "burnId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "toChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "address",
        name: "toAccount",
        type: "address"
      },
      { indexed: false, internalType: "uint64", name: "nonce", type: "uint64" }
    ],
    name: "Burn",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "period",
        type: "uint256"
      }
    ],
    name: "DelayPeriodUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "threshold",
        type: "uint256"
      }
    ],
    name: "DelayThresholdUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "bytes32", name: "id", type: "bytes32" }],
    name: "DelayedTransferAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bytes32", name: "id", type: "bytes32" },
      {
        indexed: false,
        internalType: "address",
        name: "receiver",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "DelayedTransferExecuted",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "length",
        type: "uint256"
      }
    ],
    name: "EpochLengthUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      { indexed: false, internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "EpochVolumeUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "GovernorRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MaxBurnUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "MinBurnUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bytes32",
        name: "mintId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "refChainId",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "refId",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "depositor",
        type: "address"
      }
    ],
    name: "Mint",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "PauserRemoved",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "supply",
        type: "uint256"
      }
    ],
    name: "SupplyUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Unpaused",
    type: "event"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "addGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "addPauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint64", name: "_toChainId", type: "uint64" },
      { internalType: "address", name: "_toAccount", type: "address" },
      { internalType: "uint64", name: "_nonce", type: "uint64" }
    ],
    name: "burn",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint64", name: "_toChainId", type: "uint64" },
      { internalType: "address", name: "_toAccount", type: "address" },
      { internalType: "uint64", name: "_nonce", type: "uint64" }
    ],
    name: "burnFrom",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_delta", type: "uint256" }
    ],
    name: "decreaseSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "delayPeriod",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "delayThresholds",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "delayedTransfers",
    outputs: [
      { internalType: "address", name: "receiver", type: "address" },
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "uint256", name: "timestamp", type: "uint256" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "epochLength",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "epochVolumeCaps",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "epochVolumes",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "id", type: "bytes32" }],
    name: "executeDelayedTransfer",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "governors",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_delta", type: "uint256" }
    ],
    name: "increaseSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "isGovernor",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "isPauser",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "lastOpTimestamps",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "maxBurn",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "minBurn",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "bytes", name: "_request", type: "bytes" },
      { internalType: "bytes[]", name: "_sigs", type: "bytes[]" },
      { internalType: "address[]", name: "_signers", type: "address[]" },
      { internalType: "uint256[]", name: "_powers", type: "uint256[]" }
    ],
    name: "mint",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "paused",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "pausers",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "records",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_account", type: "address" }],
    name: "removeGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "removePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceGovernor",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "renouncePauser",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_period", type: "uint256" }],
    name: "setDelayPeriod",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_thresholds", type: "uint256[]" }
    ],
    name: "setDelayThresholds",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_length", type: "uint256" }],
    name: "setEpochLength",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_caps", type: "uint256[]" }
    ],
    name: "setEpochVolumeCaps",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_amounts", type: "uint256[]" }
    ],
    name: "setMaxBurn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address[]", name: "_tokens", type: "address[]" },
      { internalType: "uint256[]", name: "_amounts", type: "uint256[]" }
    ],
    name: "setMinBurn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint256", name: "_supply", type: "uint256" }
    ],
    name: "setSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "sigsVerifier",
    outputs: [{ internalType: "contract ISigsVerifier", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "supplies",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  }
];

function cBridgeConfig(options) {
  return {
    bridgeType: "cBridge",
    timeout: CLIENT_TIME_OUT,
    ...options
  };
}
class CBridge {
  client;
  constructor(config) {
    const { timeout, endpoint } = config;
    this.client = axios.create({
      timeout,
      baseURL: endpoint
    });
  }
  // https://cbridge-docs.celer.network/developer/api-reference/gateway-estimateamt
  async getEstimatedAmount(params) {
    return (await this.client.get(`v2/estimateAmt`, {
      params
    })).data;
  }
  /**
   * Get estimated waiting time for cross-chain transfer
   *
   * @param number    srcChainId source chain ID
   * @param number    dstChainId destination chain ID
   */
  async getEstimatedWaitingTime({
    srcChainId,
    dstChainId
  }) {
    const params = {
      src_chain_id: srcChainId,
      dst_chain_id: dstChainId
    };
    return (await this.client.get(
      `v2/getLatest7DayTransferLatencyForQuery`,
      {
        params
      }
    )).data;
  }
  /**
   * Get minimum and maximum token transfer
   * Only get minimum and maximum send amount
   * @param {Address} bridgeAddress - Bridge address
   * @param {Address} tokenAddress - Token address
   * @param {PublicClient} client - Public client
   * @returns {Object} min and max amount
   */
  async getSendRange({
    bridgeAddress,
    tokenAddress,
    client
  }) {
    const contract = getContract({
      address: bridgeAddress,
      abi: POOL_TRANSFER_BRIDGE,
      client
    });
    try {
      const minAmount = await contract.read.minSend([tokenAddress]);
      const maxAmount = await contract.read.maxSend([tokenAddress]);
      return {
        min: minAmount,
        max: maxAmount
      };
    } catch (error) {
      throw new Error(
        `Failed to get cBridge minimum and maximum transfer amount: ${error}`
      );
    }
  }
  /**
   * Send token through CBridge
   * @param {WalletClient} walletClient Wallet client
   * @param {PublicClient} publicClient Wallet client
   * @param {Address} bridgeAddress Bridge address
   * @param {Object[]} bridgeABI Bridge ABI
   * @param {String} functionName Function name
   * @param {Address} address wallet/account address
   * @param {Object[]} args Function arguments
   * @returns
   */
  async sendToken({
    walletClient,
    publicClient,
    bridgeAddress,
    fromChainId,
    address,
    isPegged,
    peggedConfig,
    args
  }) {
    try {
      const transferType = this.getTransferType({
        peggedConfig,
        fromChainId
      });
      const ABI = this.getABI({
        isPegged,
        transferType,
        peggedConfig
      });
      const functionName = this.getTransferFunction({
        isPegged,
        transferType
      });
      const cBridgeArgs = {
        address: bridgeAddress,
        abi: ABI,
        functionName,
        account: address,
        args
      };
      const gas = await publicClient.estimateContractGas(cBridgeArgs);
      const gasPrice = await publicClient.getGasPrice();
      const hash = await walletClient.writeContract({
        ...cBridgeArgs,
        gas,
        gasPrice
      });
      return hash;
    } catch (error) {
      throw new Error(`Failed to send CBridge token: ${error}`);
    }
  }
  /**
   * Get cBridge contract address from cross chain transfer
   *
   * @param fromChainId Chain ID of the source chain
   * @param isPegged Pool-based transfer(xLiquidity) - false
   *                 Canonical Mapping Transfer(xAsset) - true
   * @param peggedConfig Pegged pair configuration
   * @param chainConfig Chain configuration
   */
  getTransferAddress({
    fromChainId,
    isPegged,
    peggedConfig,
    chainConfig
  }) {
    if (isPegged) {
      if (peggedConfig?.org_chain_id === fromChainId) {
        return peggedConfig.pegged_deposit_contract_addr;
      } else if (peggedConfig?.pegged_chain_id === fromChainId) {
        return peggedConfig.pegged_burn_contract_addr;
      }
    } else {
      if (chainConfig?.contract_addr) {
        return chainConfig?.contract_addr;
      }
    }
    throw new Error("No cBridge bridge address found");
  }
  /**
   * Get cBridge transfer parameters
   *
   * @param amount Send amount
   * @param isPegged Pool-based transfer(xLiquidity) - false
   *                Canonical Mapping Transfer(xAsset) - true
   * @param toChainId Chain ID of the destination chain
   * @param tokenAddress Address of ERC20 token
   * @param address User address
   * @param maxSlippage Maximum slippage
   * @param transferType Transfer type - deposit | withdraw
   * @param peggedConfig Pegged pair configuration
   * @param nonce Nonce current timestamp
   */
  getTransferParams({
    amount,
    isPegged,
    toChainId,
    tokenAddress,
    address,
    maxSlippage,
    transferType,
    peggedConfig,
    nonce
  }) {
    return isPegged === false ? [address, tokenAddress, amount, toChainId, nonce, maxSlippage] : transferType === "deposit" ? [tokenAddress, amount, toChainId, address, nonce] : transferType === "withdraw" ? peggedConfig?.bridge_version === 0 ? [tokenAddress, amount, address, nonce] : [tokenAddress, amount, toChainId, address, nonce] : null;
  }
  /**
   * Get cross chain transfer ABI
   *
   * @param isPegged Pool-based transfer(xLiquidity) - false
   *               Canonical Mapping Transfer(xAsset) - true
   * @param transferType Transfer type - deposit | withdraw
   * @param peggedConfig Pegged pair configuration
   */
  getABI({ isPegged, transferType, peggedConfig }) {
    return isPegged === false || !peggedConfig ? POOL_TRANSFER_BRIDGE : transferType === "deposit" ? peggedConfig?.vault_version === 0 ? ORIGINAL_TOKEN_VAULT : ORIGINAL_TOKEN_VAULT_V2 : transferType === "withdraw" ? peggedConfig?.bridge_version === 0 ? PEGGED_TOKEN_BRIDGE : PEGGED_TOKEN_BRIDGE_V2 : void 0;
  }
  /**
   * Get cross chain transfer function name
   *
   * @param isPegged
   * @returns string
   */
  getTransferFunction({ isPegged, transferType }) {
    return isPegged === false ? "send" : transferType === "deposit" ? "deposit" : transferType === "withdraw" ? "burn" : "";
  }
  /**
   * Get transfer type
   */
  getTransferType({
    peggedConfig,
    fromChainId
  }) {
    if (peggedConfig?.org_chain_id === fromChainId) {
      return "deposit";
    }
    if (peggedConfig?.pegged_chain_id === fromChainId) {
      return "withdraw";
    }
    return void 0;
  }
  /**
   * Generate cBridge transfer arguments
   */
  getArguments({
    isPegged,
    peggedConfig,
    chainConfig,
    amount,
    fromChainId,
    toChainId,
    tokenAddress,
    userAddress,
    maxSlippage,
    nonce
  }) {
    const transferType = this.getTransferType({
      peggedConfig,
      fromChainId
    });
    const functionName = this.getTransferFunction({ isPegged });
    const bridgeABI = this.getABI({
      isPegged,
      transferType,
      peggedConfig
    });
    const bridgeAddress = this.getTransferAddress({
      fromChainId: toChainId,
      isPegged,
      peggedConfig,
      chainConfig
    });
    const args = this.getTransferParams({
      amount,
      isPegged,
      toChainId,
      tokenAddress,
      address: userAddress,
      maxSlippage,
      transferType,
      peggedConfig,
      nonce
    });
    return {
      address: bridgeAddress,
      abi: bridgeABI,
      functionName,
      account: userAddress,
      args
    };
  }
  /**
   * validate token and contract information
   */
  validateCBridgeToken = async ({
    isPegged,
    fromChainId,
    fromTokenAddress,
    fromTokenSymbol,
    fromTokenDecimals,
    bridgeAddress,
    toChainId,
    toTokenAddress,
    toTokenSymbol,
    toTokenDecimals,
    amount,
    cBridgeEndpoint
  }) => {
    try {
      if (amount <= 0) {
        console.log("Invalid amount", amount);
        return false;
      }
      if (!fromChainId || !toChainId || !fromTokenAddress || !bridgeAddress || !fromTokenSymbol || !fromTokenDecimals || !toTokenAddress || !toTokenSymbol || !toTokenDecimals || !amount || !cBridgeEndpoint) {
        console.log("Failed to get cBridge token address validation params");
        console.log("isPegged", isPegged);
        console.log("fromChainId", fromChainId);
        console.log("fromTokenAddress", fromTokenAddress);
        console.log("fromTokenSymbol", fromTokenSymbol);
        console.log("bridgeAddress", bridgeAddress);
        console.log("toChainId", toChainId);
        console.log("toTokenAddress", toTokenAddress);
        console.log("toTokenSymbol", toTokenSymbol);
        console.log("amount", amount);
        console.log("fromTokenDecimals", fromTokenDecimals);
        console.log("toTokenDecimals", toTokenDecimals);
        console.log("cBridgeEndpoint", cBridgeEndpoint);
        return false;
      }
      if (!isEvmAddress(fromTokenAddress) || !isEvmAddress(bridgeAddress)) {
        console.log("Invalid evm address", fromTokenAddress, bridgeAddress);
        return false;
      }
      const { data: cBridgeConfig2 } = await axios.get(
        `${cBridgeEndpoint}`,
        { timeout: VALIDATION_API_TIMEOUT }
      );
      if (!cBridgeConfig2) {
        console.log("failed to get cBridge API config");
        return false;
      }
      if (isPegged === true) {
        const peggedToken = cBridgeConfig2.pegged_pair_configs.filter((pair) => {
          const orgToken = pair.org_token?.token;
          const peggedToken2 = pair.pegged_token?.token;
          return pair.pegged_deposit_contract_addr === bridgeAddress && pair?.org_chain_id === fromChainId && orgToken?.address === fromTokenAddress && orgToken?.symbol === fromTokenSymbol && orgToken?.decimal === fromTokenDecimals && peggedToken2?.address === toTokenAddress && peggedToken2?.symbol === toTokenSymbol && peggedToken2?.decimal === toTokenDecimals && pair?.pegged_chain_id === toChainId || pair?.pegged_burn_contract_addr === bridgeAddress && pair?.pegged_chain_id === fromChainId && peggedToken2?.address === fromTokenAddress && peggedToken2?.symbol === fromTokenSymbol && peggedToken2?.decimal === fromTokenDecimals && orgToken?.address === toTokenAddress && orgToken?.symbol === toTokenSymbol && orgToken?.decimal === toTokenDecimals && pair?.org_chain_id === toChainId;
        });
        if (!!peggedToken && peggedToken.length > 0) {
          console.log("cBridge pegged token info matched", peggedToken);
          return true;
        }
        console.log("Can not find cBridge pegged info");
        console.log("-- isPegged", isPegged);
        console.log("-- fromChainId", fromChainId);
        console.log("-- fromTokenAddress", fromTokenAddress);
        console.log("-- fromTokenSymbol", fromTokenSymbol);
        console.log("-- toChainId", toChainId);
        console.log("-- toTokenAddress", toTokenAddress);
        console.log("-- toTokenSymbol", toTokenSymbol);
        console.log("-- bridgeAddress", bridgeAddress);
        return false;
      } else {
        const addressInfo = cBridgeConfig2.chains.filter((chain) => {
          return chain.id === fromChainId && chain.contract_addr.toLowerCase() === bridgeAddress.toLowerCase();
        });
        const fromTokenInfo = cBridgeConfig2.chain_token[fromChainId]?.token.filter((t) => {
          return t?.token.address.toLowerCase() === fromTokenAddress.toLowerCase() && t?.token.decimal === fromTokenDecimals && t?.token.symbol === fromTokenSymbol;
        });
        const toTokenInfo = cBridgeConfig2.chain_token[toChainId]?.token.filter(
          (t) => {
            return t?.token.address.toLowerCase() === toTokenAddress.toLowerCase() && t?.token.decimal === toTokenDecimals && t?.token.symbol === toTokenSymbol;
          }
        );
        if (addressInfo?.length > 0 && fromTokenInfo?.length > 0 && toTokenInfo?.length > 0) {
          console.log("cBridge pool info matched");
          console.log("bridge contract address info", addressInfo);
          console.log("bridge from token info", fromTokenInfo);
          console.log("bridge to token info", toTokenInfo);
          return true;
        } else {
          console.log("Can not find cBridge pool info");
          console.log("-- isPegged", isPegged);
          console.log("-- fromChainId", fromChainId);
          console.log("-- fromTokenAddress", fromTokenAddress);
          console.log("-- fromTokenSymbol", fromTokenSymbol);
          console.log("-- toChainId", toChainId);
          console.log("-- toTokenAddress", toTokenAddress);
          console.log("-- toTokenSymbol", toTokenSymbol);
          console.log("-- bridgeAddress", bridgeAddress);
          return false;
        }
      }
    } catch (error) {
      console.log("cBridge token address validation error", error);
      return false;
    }
  };
}

class DeBridgeAdapter extends BaseAdapter {
  id = "deBridge";
  bridgedTokenGroups = [
    ["USDT", "USDT.e"],
    ["USDC", "USDC.e"],
    ["WETH", "WETH.e"],
    ["DAI", "DAI.e"],
    ["WBTC", "WBTC.e"],
    ["LINK", "LINK.e"],
    ["AAVE", "AAVE.e"],
    ["WOO", "WOO.e"],
    ["BUSD", "BUSD.e"],
    ["ALPHA", "ALPHA.e"],
    ["SUSHI", "SUSHI.e"],
    ["SWAP", "SWAP.e"]
  ];
  initChains() {
    const { chains, tokens } = this.config;
    const filteredChains = chains.filter((chain) => {
      const hasChainConfig = !!this.chainConfigs.find(
        (e) => e.id === chain.chainId
      );
      const isExcludedChain = this.excludedChains.includes(chain.chainId);
      const hasToken = tokens[chain.chainId]?.length > 0;
      return hasChainConfig && !isExcludedChain && hasToken;
    });
    const chainMap = /* @__PURE__ */ new Map();
    filteredChains.forEach((chain) => {
      chainMap.set(chain.chainId, chain);
    });
    this.chains = filteredChains;
    this.chainMap = chainMap;
  }
  initTokens() {
    const { tokens } = this.config;
    const tokenMap = /* @__PURE__ */ new Map();
    const symbolMap = /* @__PURE__ */ new Map();
    Object.entries(tokens).forEach(([id, chainTokens]) => {
      const chainId = Number(id);
      const filteredTokens = chainTokens.filter((token) => {
        const isExcludedToken = this.isExcludedToken({
          chainId,
          tokenSymbol: token.symbol?.toUpperCase(),
          tokenAddress: token.address
        });
        return !isExcludedToken;
      });
      if (filteredTokens.length > 0 && this.chainMap.has(chainId)) {
        symbolMap.set(chainId, /* @__PURE__ */ new Map());
        filteredTokens.forEach((token) => {
          const tokenSymbol = token.symbol?.toUpperCase();
          if (!symbolMap.get(chainId)?.get(tokenSymbol)) {
            symbolMap.get(chainId)?.set(tokenSymbol, []);
          }
          symbolMap.get(chainId)?.get(tokenSymbol)?.push(token);
        });
        tokenMap.set(chainId, filteredTokens);
      }
    });
    this.tokenMap = tokenMap;
    this.symbolMap = symbolMap;
  }
  initTransferMap() {
    const transferMap = /* @__PURE__ */ new Map();
    this.chains.forEach((fromChain) => {
      this.chains.forEach((toChain) => {
        if (fromChain.chainId !== toChain.chainId) {
          const fromTokens = this.tokenMap.get(fromChain.chainId) ?? [];
          const tokenPairsMap = /* @__PURE__ */ new Map();
          fromTokens.forEach((fromToken) => {
            const toTokens = this.getToTokensForPair({
              fromChainId: fromChain.chainId,
              toChainId: toChain.chainId,
              fromTokenSymbol: fromToken.symbol?.toUpperCase()
            });
            if (toTokens?.length) {
              const tokenPairs = [];
              toTokens.forEach((toToken) => {
                tokenPairs.push({
                  fromChainId: fromChain.chainId,
                  toChainId: toChain.chainId,
                  fromTokenAddress: fromToken.address,
                  toTokenAddress: toToken.address,
                  fromToken,
                  toToken
                });
              });
              tokenPairsMap.set(fromToken.address?.toLowerCase(), tokenPairs);
            }
          });
          if (tokenPairsMap.size > 0) {
            if (!transferMap.has(fromChain.chainId)) {
              transferMap.set(
                fromChain.chainId,
                /* @__PURE__ */ new Map()
              );
            }
            transferMap.get(fromChain.chainId)?.set(toChain.chainId, tokenPairsMap);
          }
        }
      });
    });
    this.transferMap = transferMap;
  }
  getChainId(chain) {
    return chain.chainId;
  }
  getTokenBaseInfo({
    chainId,
    token
  }) {
    return {
      name: token.name,
      symbol: token.symbol,
      address: token.address,
      decimals: token.decimals,
      ...this.getTokenDisplaySymbolAndIcon({
        chainId,
        tokenAddress: token.address,
        defaultSymbol: token.symbol,
        icon: token.logoURI
      })
    };
  }
  getTokenByAddress({
    chainId,
    address
  }) {
    if (chainId && address) {
      const tokens = this.tokenMap.get(Number(chainId));
      const target = tokens?.find(
        (item) => isSameAddress(item.address, address)
      );
      return target;
    }
  }
}

function deBridge(params) {
  return {
    id: "deBridge",
    ...params
  };
}

function deBridgeConfig(options) {
  return {
    bridgeType: "deBridge",
    timeout: CLIENT_TIME_OUT,
    ...options
  };
}
class DeBridge {
  client;
  statsClient;
  constructor(config) {
    const { timeout, endpoint, statsEndpoint } = config;
    this.client = axios.create({
      timeout,
      baseURL: endpoint
    });
    this.statsClient = axios.create({
      timeout,
      baseURL: statsEndpoint
    });
  }
  // https://deswap.debridge.finance/v1.0/#/DLN/DlnOrderControllerV10_createOrder
  async createTxQuote(urlParams) {
    return (await this.client.get(
      `/dln/order/create-tx?${urlParams.toString()}`
    )).data;
  }
  /**
   * Get estimated fees from transaction quote API
   * @param {number} fromChainId - Chain ID of the source chain
   * @param {Address} fromTokenAddress - Address of ERC20 token on the source chain
   * @param {BigInt} amount - Send amount
   * @param {number} toChainId - Chain ID of the destination chain
   * @param {Address} toTokenAddress - Address of ERC20 token on the destination chain
   * @param {Address} userAddress - user/account address
   */
  async getEstimatedFees({
    fromChainId,
    fromTokenAddress,
    amount,
    toChainId,
    toTokenAddress,
    userAddress,
    toUserAddress,
    affiliateFeePercent = 0,
    accesstoken = "",
    referralCode,
    prependOperatingExpenses = false
  }) {
    try {
      const deBridgeParams = {
        srcChainId: fromChainId,
        srcChainTokenIn: fromTokenAddress,
        srcChainTokenInAmount: amount,
        dstChainId: toChainId,
        dstChainTokenOut: toTokenAddress,
        prependOperatingExpenses,
        affiliateFeePercent,
        dstChainTokenOutRecipient: toUserAddress || userAddress,
        dstChainOrderAuthorityAddress: toUserAddress || userAddress,
        srcChainOrderAuthorityAddress: userAddress
      };
      if (referralCode) {
        deBridgeParams.referralCode = referralCode;
      }
      if (accesstoken) {
        deBridgeParams.accesstoken = accesstoken;
      }
      const urlParams = new URLSearchParams(deBridgeParams);
      const deBridgeQuote = await this.createTxQuote(urlParams);
      return deBridgeQuote;
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        throw new Error(error.response.data.errorId);
      } else {
        throw new Error(`${error.message || error}`);
      }
    }
  }
  // https://deswap.debridge.finance/v1.0/#/DLN/DlnOrderControllerV10_getOrder
  async getOrder({ id }) {
    return (await this.client.get(`/dln/order/${id}`)).data;
  }
  // https://stats-api.dln.trade/swagger/index.html
  /**
   * Get list of orders by filters
   * @param address Account address
   * @param pageId Page number
   * @param pageSize Records per page
   * @param fromChainIds Source chain IDs
   * @param toChainIds Destination chain IDs
   */
  async getStatsHistory({
    address,
    pageId = 0,
    pageSize = 20,
    fromChainIds = [],
    toChainIds = []
  }) {
    return (await this.statsClient.post("/Orders/filteredList", {
      filter: address,
      skip: pageId,
      // page number
      take: pageSize,
      // data per page
      giveChainIds: fromChainIds,
      takeChainIds: toChainIds
    })).data;
  }
  /**
   * Send token via DeBridge
   * @param {WalletClient} walletClient Wallet client
   * @param {Address} bridgeAddress Bridge address
   * @param {String} data Transaction data
   * @param {BigInt} amount Send amount
   * @param {Address} address wallet/account address
   * @returns {Hash} transaction hash
   */
  async sendToken({
    walletClient,
    bridgeAddress,
    data,
    amount,
    address
  }) {
    try {
      const hash = await walletClient.sendTransaction({
        to: bridgeAddress,
        data,
        value: amount,
        account: address,
        chain: walletClient.chain
      });
      return hash;
    } catch (error) {
      throw new Error(`Failed to send DeBridge token: ${error}`);
    }
  }
  validateDeBridgeToken = async ({
    fromChainId,
    fromChainType,
    toChainId,
    toChainType,
    fromTokenAddress,
    fromTokenSymbol,
    fromTokenDecimals,
    fromBridgeAddress,
    toTokenAddress,
    toTokenSymbol,
    toTokenDecimals,
    amount,
    deBridgeEndpoint
  }) => {
    try {
      if (Number(amount) <= 0) {
        console.log("Invalid deBridge amount", amount);
        return false;
      }
      if (!fromChainId || !fromChainType || !fromTokenAddress || !toTokenAddress || !fromTokenSymbol || !toTokenSymbol || !toChainId || !toChainType || !amount || !fromTokenDecimals || !fromBridgeAddress && fromChainType === "evm" || !toTokenDecimals || !deBridgeEndpoint) {
        console.log("Invalid deBridge token validation params");
        console.log("-- fromChainId", fromChainId);
        console.log("-- fromChainType", fromChainType);
        console.log("-- fromTokenSymbol", fromTokenSymbol);
        console.log("-- fromTokenAddress", fromTokenAddress);
        console.log("-- fromTokenDecimals", fromTokenDecimals);
        console.log("-- fromBridgeAddress", fromBridgeAddress);
        console.log("-- toChainId", toChainId);
        console.log("-- toChainType", toChainType);
        console.log("-- toTokenSymbol", toTokenSymbol);
        console.log("-- toTokenAddress", toTokenAddress);
        console.log("-- toTokenDecimals", toTokenDecimals);
        console.log("-- amount", amount);
        console.log("-- deBridgeEndpoint", deBridgeEndpoint);
        return false;
      }
      const isValidFromToken = isValidTokenAddress({
        contractAddress: fromTokenAddress,
        chainType: fromChainType,
        isSourceChain: true
      });
      const isValidToToken = isValidTokenAddress({
        contractAddress: toTokenAddress,
        chainType: toChainType,
        isSourceChain: true
      });
      if (!isValidFromToken || !isValidToToken) {
        console.log(
          "Invalid deBridge bridge token address",
          fromTokenAddress,
          toTokenAddress
        );
        return false;
      }
      if (fromChainType !== "solana") {
        const isValidBridgeContractAddress = isValidTokenAddress({
          contractAddress: fromBridgeAddress,
          chainType: fromChainType,
          isSourceChain: true
        });
        if (!isValidBridgeContractAddress) {
          console.log(
            "Invalid deBridge bridge contract address",
            fromBridgeAddress
          );
          return false;
        }
      }
      const fromRequest = axios.get(`${deBridgeEndpoint}/token-list?chainId=${fromChainId}`, {
        timeout: VALIDATION_API_TIMEOUT
      });
      const toRequest = axios.get(`${deBridgeEndpoint}/token-list?chainId=${toChainId}`, {
        timeout: VALIDATION_API_TIMEOUT
      });
      const [fromTokenList, toTokenList] = await Promise.allSettled([
        fromRequest,
        toRequest
      ]);
      if (fromTokenList.status === "fulfilled" && toTokenList.status === "fulfilled") {
        const fromTokenAddr = fromChainType === "solana" ? fromTokenAddress : fromTokenAddress.toLowerCase();
        const toTokenAddr = toChainType === "solana" ? toTokenAddress : toTokenAddress.toLowerCase();
        const fromToken = fromTokenList?.value?.data.tokens[fromTokenAddr];
        const toToken = toTokenList?.value?.data.tokens[toTokenAddr];
        if (!toToken) {
          console.log("Can not find toToken info");
        }
        if (!fromToken) {
          console.log("Can not find fromToken info");
        }
        if (!!fromToken && fromToken?.address.toLowerCase() === fromTokenAddress.toLowerCase() && fromToken?.symbol === fromTokenSymbol && fromToken?.decimals === fromTokenDecimals && !!toToken && toToken?.address.toLowerCase() === toTokenAddress.toLowerCase() && toToken?.symbol === toTokenSymbol && toToken?.decimals === toTokenDecimals) {
          console.log("deBridge token info matched", fromToken);
          return true;
        }
      } else {
        console.log(
          "Failed to get deBridge API token info",
          fromTokenList,
          toTokenList
        );
        return false;
      }
      console.log("Could not find deBridge token info");
      console.log("-- fromChainId", fromChainId);
      console.log("-- from fromChainType", fromChainType);
      console.log("-- from tokenSymbol", fromTokenSymbol);
      console.log("-- from tokenAddress", fromTokenAddress);
      console.log("-- from TokenDecimals", fromTokenDecimals);
      console.log("-- to ChainId", toChainId);
      console.log("-- to ChainType", toChainType);
      console.log("-- to tokenSymbol", toTokenSymbol);
      console.log("-- to tokenAddress", toTokenAddress);
      console.log("-- to TokenDecimals", toTokenDecimals);
      console.log("-- amount", amount);
      return false;
    } catch (error) {
      console.log("deBridge token validation error", error);
      return false;
    }
  };
}

class LayerZeroAdapter extends BaseAdapter {
  id = "layerZero";
  bridgedTokenGroups = [];
  initChains() {
    const { chains, tokens } = this.config;
    const filteredChains = chains.filter((chain) => {
      const hasChainConfig = !!this.chainConfigs.find(
        (e) => e.id === chain.chainId
      );
      const isExcludedChain = this.excludedChains.includes(chain.chainId);
      const hasToken = tokens[chain.chainId]?.length > 0;
      return hasChainConfig && !isExcludedChain && hasToken;
    });
    const chainMap = /* @__PURE__ */ new Map();
    filteredChains.forEach((chain) => {
      chainMap.set(chain.chainId, chain);
    });
    this.chains = filteredChains;
    this.chainMap = chainMap;
  }
  initTokens() {
    const { tokens } = this.config;
    const tokenMap = /* @__PURE__ */ new Map();
    const symbolMap = /* @__PURE__ */ new Map();
    Object.entries(tokens).forEach(([id, chainTokens]) => {
      const chainId = Number(id);
      const filteredTokens = chainTokens.filter((token) => {
        const isExcludedToken = this.isExcludedToken({
          chainId,
          tokenSymbol: token.symbol?.toUpperCase(),
          tokenAddress: token.address
        });
        return !isExcludedToken;
      });
      if (filteredTokens.length > 0 && this.chainMap.has(chainId)) {
        symbolMap.set(chainId, /* @__PURE__ */ new Map());
        filteredTokens.forEach((token) => {
          const tokenSymbol = token.symbol?.toUpperCase();
          if (!symbolMap.get(chainId)?.get(tokenSymbol)) {
            symbolMap.get(chainId)?.set(tokenSymbol, []);
          }
          symbolMap.get(chainId)?.get(tokenSymbol)?.push(token);
        });
        tokenMap.set(chainId, filteredTokens);
      }
    });
    this.tokenMap = tokenMap;
    this.symbolMap = symbolMap;
  }
  initTransferMap() {
    const transferMap = /* @__PURE__ */ new Map();
    this.chains.forEach((fromChain) => {
      this.chains.forEach((toChain) => {
        if (fromChain.chainId !== toChain.chainId && fromChain.network === toChain.network) {
          const fromTokens = this.tokenMap.get(fromChain.chainId) ?? [];
          const tokenPairsMap = /* @__PURE__ */ new Map();
          fromTokens.forEach((fromToken) => {
            const toTokens = this.getToTokensForPair({
              fromChainId: fromChain.chainId,
              toChainId: toChain.chainId,
              fromTokenSymbol: fromToken.symbol?.toUpperCase()
            });
            if (toTokens?.length) {
              const tokenPairs = [];
              toTokens.forEach((toToken) => {
                tokenPairs.push({
                  fromChainId: fromChain.chainId,
                  toChainId: toChain.chainId,
                  fromTokenAddress: fromToken.address,
                  toTokenAddress: toToken.address,
                  fromToken,
                  toToken
                });
              });
              tokenPairsMap.set(fromToken.address?.toLowerCase(), tokenPairs);
            }
          });
          if (tokenPairsMap.size > 0) {
            if (!transferMap.has(fromChain.chainId)) {
              transferMap.set(
                fromChain.chainId,
                /* @__PURE__ */ new Map()
              );
            }
            transferMap.get(fromChain.chainId)?.set(toChain.chainId, tokenPairsMap);
          }
        }
      });
    });
    this.transferMap = transferMap;
  }
  getChainId(chain) {
    return chain.chainId;
  }
  getTokenBaseInfo({
    chainId,
    token
  }) {
    return {
      name: token.name,
      // TODO
      symbol: token.symbol,
      address: token.address,
      decimals: token.decimals,
      ...this.getTokenDisplaySymbolAndIcon({
        chainId,
        tokenAddress: token.address,
        defaultSymbol: token.symbol
      })
    };
  }
}

function layerZero(params) {
  return {
    id: "layerZero",
    ...params
  };
}

const ERC20_TOKEN = [
  {
    inputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "owner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "spender",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "value",
        type: "uint256"
      }
    ],
    name: "Approval",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "from", type: "address" },
      { indexed: true, internalType: "address", name: "to", type: "address" },
      {
        indexed: false,
        internalType: "uint256",
        name: "value",
        type: "uint256"
      }
    ],
    name: "Transfer",
    type: "event"
  },
  {
    constant: true,
    inputs: [],
    name: "_decimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "_name",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "_symbol",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: true,
    inputs: [
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "address", name: "spender", type: "address" }
    ],
    name: "allowance",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: false,
    inputs: [
      { internalType: "address", name: "spender", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "approve",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: true,
    inputs: [{ internalType: "address", name: "account", type: "address" }],
    name: "balanceOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: false,
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "burn",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: false,
    inputs: [
      { internalType: "address", name: "spender", type: "address" },
      { internalType: "uint256", name: "subtractedValue", type: "uint256" }
    ],
    name: "decreaseAllowance",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "getOwner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: false,
    inputs: [
      { internalType: "address", name: "spender", type: "address" },
      { internalType: "uint256", name: "addedValue", type: "uint256" }
    ],
    name: "increaseAllowance",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: false,
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "mint",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "name",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: false,
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "symbol",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: true,
    inputs: [],
    name: "totalSupply",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function"
  },
  {
    constant: false,
    inputs: [
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "transfer",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: false,
    inputs: [
      { internalType: "address", name: "sender", type: "address" },
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "transferFrom",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    constant: false,
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "function"
  }
];

const CAKE_PROXY_OFT_ABI = [
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint8", name: "_sharedDecimals", type: "uint8" },
      { internalType: "address", name: "_lzEndpoint", type: "address" }
    ],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    inputs: [
      { internalType: "uint256", name: "cap", type: "uint256" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "ExceedInboundCap",
    type: "error"
  },
  {
    inputs: [
      { internalType: "uint256", name: "cap", type: "uint256" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "ExceedOutboundCap",
    type: "error"
  },
  { inputs: [], name: "NotOperator", type: "error" },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "uint16",
        name: "_srcChainId",
        type: "uint16"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "_srcAddress",
        type: "bytes"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "_nonce",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "_hash",
        type: "bytes32"
      }
    ],
    name: "CallOFTReceivedSuccess",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "srcChainId",
        type: "uint16"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "srcAddress",
        type: "bytes"
      },
      { indexed: false, internalType: "uint64", name: "nonce", type: "uint64" }
    ],
    name: "DropFailedMessage",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "FallbackWithdraw",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "_srcChainId",
        type: "uint16"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "_srcAddress",
        type: "bytes"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "_nonce",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "_payload",
        type: "bytes"
      },
      { indexed: false, internalType: "bytes", name: "_reason", type: "bytes" }
    ],
    name: "MessageFailed",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "_address",
        type: "address"
      }
    ],
    name: "NonContractAddress",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "previousOwner",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "newOwner",
        type: "address"
      }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "uint16",
        name: "_srcChainId",
        type: "uint16"
      },
      { indexed: true, internalType: "address", name: "_to", type: "address" },
      {
        indexed: false,
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      }
    ],
    name: "ReceiveFromChain",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "_srcChainId",
        type: "uint16"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "_srcAddress",
        type: "bytes"
      },
      {
        indexed: false,
        internalType: "uint64",
        name: "_nonce",
        type: "uint64"
      },
      {
        indexed: false,
        internalType: "bytes32",
        name: "_payloadHash",
        type: "bytes32"
      }
    ],
    name: "RetryMessageSuccess",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "uint16",
        name: "_dstChainId",
        type: "uint16"
      },
      {
        indexed: true,
        internalType: "address",
        name: "_from",
        type: "address"
      },
      {
        indexed: true,
        internalType: "bytes32",
        name: "_toAddress",
        type: "bytes32"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "_amount",
        type: "uint256"
      }
    ],
    name: "SendToChain",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "uint16", name: "feeBp", type: "uint16" }
    ],
    name: "SetDefaultFeeBp",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "dstchainId",
        type: "uint16"
      },
      { indexed: false, internalType: "bool", name: "enabled", type: "bool" },
      { indexed: false, internalType: "uint16", name: "feeBp", type: "uint16" }
    ],
    name: "SetFeeBp",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "feeOwner",
        type: "address"
      }
    ],
    name: "SetFeeOwner",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "uint16",
        name: "chainId",
        type: "uint16"
      },
      { indexed: false, internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "SetInboundCap",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "_dstChainId",
        type: "uint16"
      },
      { indexed: false, internalType: "uint16", name: "_type", type: "uint16" },
      {
        indexed: false,
        internalType: "uint256",
        name: "_minDstGas",
        type: "uint256"
      }
    ],
    name: "SetMinDstGas",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "newOperator",
        type: "address"
      }
    ],
    name: "SetOperator",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "uint16",
        name: "chainId",
        type: "uint16"
      },
      { indexed: false, internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "SetOutboundCap",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "precrime",
        type: "address"
      }
    ],
    name: "SetPrecrime",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "_remoteChainId",
        type: "uint16"
      },
      { indexed: false, internalType: "bytes", name: "_path", type: "bytes" }
    ],
    name: "SetTrustedRemote",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint16",
        name: "_remoteChainId",
        type: "uint16"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "_remoteAddress",
        type: "bytes"
      }
    ],
    name: "SetTrustedRemoteAddress",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "bool",
        name: "_useCustomAdapterParams",
        type: "bool"
      }
    ],
    name: "SetUseCustomAdapterParams",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "addr", type: "address" },
      {
        indexed: false,
        internalType: "bool",
        name: "isWhitelist",
        type: "bool"
      }
    ],
    name: "SetWhitelist",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "account",
        type: "address"
      }
    ],
    name: "Unpaused",
    type: "event"
  },
  {
    inputs: [],
    name: "BP_DENOMINATOR",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "NO_EXTRA_GAS",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "PT_SEND",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "PT_SEND_AND_CALL",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "", type: "uint16" },
      { internalType: "bytes", name: "", type: "bytes" },
      { internalType: "uint64", name: "", type: "uint64" }
    ],
    name: "amountsForCall",
    outputs: [
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "bool", name: "credited", type: "bool" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_srcAddress", type: "bytes" },
      { internalType: "uint64", name: "_nonce", type: "uint64" },
      { internalType: "bytes32", name: "_from", type: "bytes32" },
      { internalType: "address", name: "_to", type: "address" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "bytes", name: "_payload", type: "bytes" },
      { internalType: "uint256", name: "_gasForCall", type: "uint256" }
    ],
    name: "callOnOFTReceived",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToFeeBps",
    outputs: [
      { internalType: "uint16", name: "feeBP", type: "uint16" },
      { internalType: "bool", name: "enabled", type: "bool" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToInboundCap",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToLastReceivedTimestamp",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToLastSentTimestamp",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToOutboundCap",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToReceivedTokenAmount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "chainIdToSentTokenAmount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "circulatingSupply",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "defaultFeeBp",
    outputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "srcChainId", type: "uint16" },
      { internalType: "bytes", name: "srcAddress", type: "bytes" },
      { internalType: "uint64", name: "nonce", type: "uint64" }
    ],
    name: "dropFailedMessage",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "bytes32", name: "_toAddress", type: "bytes32" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "bytes", name: "_payload", type: "bytes" },
      { internalType: "uint64", name: "_dstGasForCall", type: "uint64" },
      { internalType: "bool", name: "_useZro", type: "bool" },
      { internalType: "bytes", name: "_adapterParams", type: "bytes" }
    ],
    name: "estimateSendAndCallFee",
    outputs: [
      { internalType: "uint256", name: "nativeFee", type: "uint256" },
      { internalType: "uint256", name: "zroFee", type: "uint256" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "bytes32", name: "_toAddress", type: "bytes32" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "bool", name: "_useZro", type: "bool" },
      { internalType: "bytes", name: "_adapterParams", type: "bytes" }
    ],
    name: "estimateSendFee",
    outputs: [
      { internalType: "uint256", name: "nativeFee", type: "uint256" },
      { internalType: "uint256", name: "zroFee", type: "uint256" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "", type: "uint16" },
      { internalType: "bytes", name: "", type: "bytes" },
      { internalType: "uint64", name: "", type: "uint64" }
    ],
    name: "failedMessages",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "fallbackWithdraw",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "feeOwner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_srcAddress", type: "bytes" }
    ],
    name: "forceResumeReceive",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_version", type: "uint16" },
      { internalType: "uint16", name: "_chainId", type: "uint16" },
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint256", name: "_configType", type: "uint256" }
    ],
    name: "getConfig",
    outputs: [{ internalType: "bytes", name: "", type: "bytes" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_remoteChainId", type: "uint16" }
    ],
    name: "getTrustedRemoteAddress",
    outputs: [{ internalType: "bytes", name: "", type: "bytes" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_srcAddress", type: "bytes" }
    ],
    name: "isTrustedRemote",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "lzEndpoint",
    outputs: [
      {
        internalType: "contract ILayerZeroEndpoint",
        name: "",
        type: "address"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_srcAddress", type: "bytes" },
      { internalType: "uint64", name: "_nonce", type: "uint64" },
      { internalType: "bytes", name: "_payload", type: "bytes" }
    ],
    name: "lzReceive",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "", type: "uint16" },
      { internalType: "uint16", name: "", type: "uint16" }
    ],
    name: "minDstGasLookup",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_srcAddress", type: "bytes" },
      { internalType: "uint64", name: "_nonce", type: "uint64" },
      { internalType: "bytes", name: "_payload", type: "bytes" }
    ],
    name: "nonblockingLzReceive",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "operator",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "outboundAmount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "paused",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "precrime",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "uint256", name: "_amount", type: "uint256" }
    ],
    name: "quoteOFTFee",
    outputs: [{ internalType: "uint256", name: "fee", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_srcAddress", type: "bytes" },
      { internalType: "uint64", name: "_nonce", type: "uint64" },
      { internalType: "bytes", name: "_payload", type: "bytes" }
    ],
    name: "retryMessage",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_from", type: "address" },
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "bytes32", name: "_toAddress", type: "bytes32" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint256", name: "_minAmount", type: "uint256" },
      { internalType: "bytes", name: "_payload", type: "bytes" },
      { internalType: "uint64", name: "_dstGasForCall", type: "uint64" },
      {
        components: [
          {
            internalType: "address payable",
            name: "refundAddress",
            type: "address"
          },
          {
            internalType: "address",
            name: "zroPaymentAddress",
            type: "address"
          },
          { internalType: "bytes", name: "adapterParams", type: "bytes" }
        ],
        internalType: "struct ICommonOFT.LzCallParams",
        name: "_callParams",
        type: "tuple"
      }
    ],
    name: "sendAndCall",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_from", type: "address" },
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "bytes32", name: "_toAddress", type: "bytes32" },
      { internalType: "uint256", name: "_amount", type: "uint256" },
      { internalType: "uint256", name: "_minAmount", type: "uint256" },
      {
        components: [
          {
            internalType: "address payable",
            name: "refundAddress",
            type: "address"
          },
          {
            internalType: "address",
            name: "zroPaymentAddress",
            type: "address"
          },
          { internalType: "bytes", name: "adapterParams", type: "bytes" }
        ],
        internalType: "struct ICommonOFT.LzCallParams",
        name: "_callParams",
        type: "tuple"
      }
    ],
    name: "sendFrom",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_version", type: "uint16" },
      { internalType: "uint16", name: "_chainId", type: "uint16" },
      { internalType: "uint256", name: "_configType", type: "uint256" },
      { internalType: "bytes", name: "_config", type: "bytes" }
    ],
    name: "setConfig",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "_feeBp", type: "uint16" }],
    name: "setDefaultFeeBp",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "bool", name: "_enabled", type: "bool" },
      { internalType: "uint16", name: "_feeBp", type: "uint16" }
    ],
    name: "setFeeBp",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_feeOwner", type: "address" }],
    name: "setFeeOwner",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "chainId", type: "uint16" },
      { internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "setInboundCap",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_dstChainId", type: "uint16" },
      { internalType: "uint16", name: "_packetType", type: "uint16" },
      { internalType: "uint256", name: "_minGas", type: "uint256" }
    ],
    name: "setMinDstGas",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "newOperator", type: "address" }],
    name: "setOperator",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "chainId", type: "uint16" },
      { internalType: "uint256", name: "cap", type: "uint256" }
    ],
    name: "setOutboundCap",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_precrime", type: "address" }],
    name: "setPrecrime",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "_version", type: "uint16" }],
    name: "setReceiveVersion",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "_version", type: "uint16" }],
    name: "setSendVersion",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_srcChainId", type: "uint16" },
      { internalType: "bytes", name: "_path", type: "bytes" }
    ],
    name: "setTrustedRemote",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint16", name: "_remoteChainId", type: "uint16" },
      { internalType: "bytes", name: "_remoteAddress", type: "bytes" }
    ],
    name: "setTrustedRemoteAddress",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "bool", name: "_useCustomAdapterParams", type: "bool" }
    ],
    name: "setUseCustomAdapterParams",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "addr", type: "address" },
      { internalType: "bool", name: "isWhitelist", type: "bool" }
    ],
    name: "setWhitelist",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "sharedDecimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes4", name: "interfaceId", type: "bytes4" }],
    name: "supportsInterface",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "token",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint16", name: "", type: "uint16" }],
    name: "trustedRemoteLookup",
    outputs: [{ internalType: "bytes", name: "", type: "bytes" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "useCustomAdapterParams",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "whitelist",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  }
];

function removeAfterDecimals(num, decimals = 8) {
  const numStr = formatSmallNumber(Number(num));
  const parts = numStr.split(".");
  if (parts.length < 2) return num;
  const fractionalPart = parts[1].slice(0, decimals);
  const result = parts[0] + "." + fractionalPart;
  return result;
}
const formatNumber = (value, decimals = 18, useGrouping = true) => {
  const num = removeAfterDecimals(value, decimals);
  return Number(num).toLocaleString("fullwide", {
    maximumFractionDigits: decimals,
    useGrouping
  });
};
const formatSmallNumber = (x) => {
  let ret = x.toString();
  if (Math.abs(x) < 1) {
    const e = parseInt(x.toString().split("e-")[1]);
    if (e) {
      x *= Math.pow(10, e - 1);
      ret = "0." + new Array(e).join("0") + x.toString().substring(2);
    }
  } else {
    let e = parseInt(x.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      x /= Math.pow(10, e);
      ret = x.toString();
      ret += new Array(e + 1).join("0");
    }
  }
  return ret;
};

class LayerZero {
  /**
   * Send token through layerZero V1 OFT
   * https://docs.layerzero.network/v1/developers/evm/evm-guides/advanced/relayer-adapter-parameters
   *
   * @param userAddress User address
   * @param bridgeAddress Bridge address
   * @param amount Send amount
   * @param dstEndpoint Destination endpoint
   * @param publicClient Public client
   * @param walletClient Wallet client
   * @param gasAmount Gas amount
   * @param version Relayer adapter parameters version
   */
  async sendToken({
    userAddress,
    bridgeAddress,
    amount,
    dstEndpoint,
    publicClient,
    walletClient,
    gasAmount = 200000n,
    version = 1,
    airDropGas = 0n,
    dstAddress = "0x"
  }) {
    try {
      const address32Bytes = pad(userAddress, { size: 32 });
      const dstGasLimit = await publicClient.readContract({
        address: bridgeAddress,
        abi: CAKE_PROXY_OFT_ABI,
        functionName: "minDstGasLookup",
        args: [dstEndpoint, 0]
      });
      const gasLimit = dstGasLimit !== 0n && !!dstGasLimit ? dstGasLimit : gasAmount;
      const adapterParams = version === 1 ? encodePacked(["uint16", "uint256"], [version, gasLimit]) : encodePacked(
        ["uint16", "uint", "uint", "address"],
        [2, gasLimit, airDropGas, dstAddress]
      );
      const fees = await publicClient.readContract({
        address: bridgeAddress,
        abi: CAKE_PROXY_OFT_ABI,
        functionName: "estimateSendFee",
        args: [
          dstEndpoint,
          address32Bytes,
          amount,
          false,
          adapterParams
        ]
      });
      const callParams = [
        userAddress,
        "0x0000000000000000000000000000000000000000",
        // zroPaymentAddress
        adapterParams
      ];
      const nativeFee = fees[0];
      const minAmount = parseUnits(
        String(formatNumber(Number(formatUnits(amount, 18)), 8, false)),
        18
      );
      const cakeArgs = {
        address: bridgeAddress,
        abi: CAKE_PROXY_OFT_ABI,
        functionName: "sendFrom",
        args: [
          userAddress,
          dstEndpoint,
          address32Bytes,
          amount,
          minAmount,
          callParams
        ],
        value: nativeFee,
        account: userAddress
      };
      const gas = await publicClient.estimateContractGas(cakeArgs);
      const gasPrice = await publicClient.getGasPrice();
      const hash = await walletClient.writeContract({
        ...cakeArgs,
        gas,
        gasPrice
      });
      return hash;
    } catch (error) {
      throw new Error(`Failed to send CAKE token ${error}`);
    }
  }
  /**
   * Get estimate fee
   * @param bridgeAddress Bridge address
   * @param amount Send amount
   * @param dstEndpoint Destination endpoint
   * @param userAddress User address
   * @param publicClient Public client
   * @param gasAmount Gas amount
   * @param version Relayer adapter parameters version
   * @returns Estimate fee
   */
  async getEstimateFee({
    bridgeAddress,
    amount,
    dstEndpoint,
    userAddress,
    publicClient,
    gasAmount = 200000n,
    version = 1,
    airDropGas = 0n,
    dstAddress = "0x"
  }) {
    try {
      const dstGasLimit = await publicClient.readContract({
        address: bridgeAddress,
        abi: CAKE_PROXY_OFT_ABI,
        functionName: "minDstGasLookup",
        args: [dstEndpoint, 0]
      });
      const gasLimit = dstGasLimit !== 0n && !!dstGasLimit ? dstGasLimit : gasAmount;
      const address32Bytes = pad(userAddress, { size: 32 });
      const adapterParams = version === 1 ? encodePacked(["uint16", "uint256"], [version, gasLimit]) : encodePacked(
        ["uint16", "uint", "uint", "address"],
        [2, gasLimit, airDropGas, dstAddress]
      );
      const fees = await publicClient.readContract({
        address: bridgeAddress,
        abi: CAKE_PROXY_OFT_ABI,
        functionName: "estimateSendFee",
        args: [
          dstEndpoint,
          address32Bytes,
          amount,
          false,
          adapterParams
        ]
      });
      return fees;
    } catch (error) {
      throw new Error(`Failed to get estimate fee ${error}`);
    }
  }
  validateLayerZeroToken = async ({
    fromPublicClient,
    toPublicClient,
    bridgeAddress,
    fromTokenAddress,
    fromTokenSymbol,
    fromTokenDecimals,
    toTokenAddress,
    toTokenSymbol,
    toTokenDecimals,
    toBridgeAddress,
    dstEndpoint,
    amount
  }) => {
    if (Number(amount) <= 0) {
      console.log("Invalid send amount");
      return false;
    }
    if (!fromPublicClient || !bridgeAddress || !fromTokenAddress || !fromTokenDecimals || !dstEndpoint || !toTokenAddress || !toBridgeAddress || !toTokenSymbol || !toTokenDecimals || !toPublicClient) {
      console.log("Missing required parameters");
      console.log("-- publicClient", fromPublicClient);
      console.log("-- toPublicClient", toPublicClient);
      console.log("-- bridgeAddress", bridgeAddress);
      console.log("-- fromTokenAddress", fromTokenAddress);
      console.log("-- fromTokenSymbol", fromTokenSymbol);
      console.log("-- fromTokenDecimals", fromTokenDecimals);
      console.log("-- toTokenAddress", toTokenAddress);
      console.log("-- toBridgeAddress", toBridgeAddress);
      console.log("-- toTokenSymbol", toTokenSymbol);
      console.log("-- toTokenDecimals", toTokenDecimals);
      console.log("-- dstEndpoint", dstEndpoint);
      console.log("-- amount", amount);
      return false;
    }
    if (!isEvmAddress(fromTokenAddress) || !isEvmAddress(toTokenAddress) || !isEvmAddress(toBridgeAddress) || !isEvmAddress(bridgeAddress)) {
      console.log("Invalid contract address");
      console.log("-- fromTokenAddress", fromTokenAddress);
      console.log("-- toTokenAddress", toTokenAddress);
      console.log("-- toBridgeAddress", toBridgeAddress);
      console.log("-- bridgeAddress", bridgeAddress);
      return false;
    }
    const contractSymbol = await fromPublicClient.readContract({
      address: fromTokenAddress,
      abi: ERC20_TOKEN,
      functionName: "symbol"
    });
    if (contractSymbol.toLowerCase() !== fromTokenSymbol.toLowerCase()) {
      console.log(
        "Failed to match from token symbol",
        fromTokenSymbol,
        contractSymbol
      );
      return false;
    }
    const contractDecimals = await fromPublicClient.readContract({
      address: fromTokenAddress,
      abi: ERC20_TOKEN,
      functionName: "decimals"
    });
    if (fromTokenDecimals !== contractDecimals) {
      console.log("Failed to match from token decimals");
      return false;
    }
    const trustedRemoteAddress = await fromPublicClient.readContract({
      address: bridgeAddress,
      abi: CAKE_PROXY_OFT_ABI,
      functionName: "getTrustedRemoteAddress",
      args: [dstEndpoint]
    });
    if (trustedRemoteAddress.toLowerCase() !== toBridgeAddress.toLowerCase()) {
      console.log(
        "Failed to match layerZero remote contract address",
        trustedRemoteAddress,
        toBridgeAddress
      );
      return false;
    }
    const toTokenContractSymbol = await toPublicClient.readContract({
      address: toTokenAddress,
      abi: ERC20_TOKEN,
      functionName: "symbol"
    });
    if (toTokenContractSymbol.toLowerCase() !== toTokenSymbol.toLowerCase()) {
      console.log(
        "Failed to match to token symbol",
        toTokenSymbol,
        toTokenContractSymbol
      );
      return false;
    }
    const toTokenContractDecimals = await toPublicClient.readContract({
      address: toTokenAddress,
      abi: ERC20_TOKEN,
      functionName: "decimals"
    });
    if (toTokenDecimals !== toTokenContractDecimals) {
      console.log("Failed to match to token decimals");
      return false;
    }
    const supportedFromToken = await fromPublicClient.readContract({
      address: bridgeAddress,
      abi: CAKE_PROXY_OFT_ABI,
      functionName: "token"
    });
    const supportedToToken = await toPublicClient.readContract({
      address: toBridgeAddress,
      abi: CAKE_PROXY_OFT_ABI,
      functionName: "token"
    });
    if (supportedFromToken.toLowerCase() === fromTokenAddress.toLowerCase() && supportedToToken.toLowerCase() === toTokenAddress.toLowerCase()) {
      console.log("LayerZero token information matched");
      console.log("From token address", fromTokenAddress, supportedFromToken);
      console.log("To token address", toTokenAddress, supportedToToken);
      return true;
    }
    console.log("Failed to match layerZero token information");
    console.log("-- publicClient", fromPublicClient);
    console.log("-- bridgeAddress", bridgeAddress);
    console.log("-- fromTokenAddress", fromTokenAddress);
    console.log("-- fromTokenSymbol", fromTokenSymbol);
    console.log("-- toTokenAddress", toTokenAddress);
    console.log("-- toBridgeAddress", toBridgeAddress);
    console.log("-- dstEndpoint", dstEndpoint);
    return false;
  };
}

const stargateChainKey$1 = {
  ETHEREUM: 1,
  OPTIMISM: 10,
  FLARE: 14,
  BSC: 56,
  POLYGON: 137,
  FANTOM: 250,
  METIS: 1088,
  COREDAO: 1116,
  SEI: 1329,
  GRAVITY: 1625,
  KAVA: 2222,
  MANTLE: 5e3,
  KLAYTN: 8217,
  BASE: 8453,
  IOTA: 8822,
  ARBITRUM: 42161,
  AVALANCHE: 43114,
  LINEA: 59144,
  TAIKO: 167e3,
  SCROLL: 534352,
  AURORA: 1313161554,
  RARIBLE: 1380012617
};

function utf8ToHex(utf8Str) {
  return Array.from(utf8Str).map((char) => char.charCodeAt(0).toString(16).padStart(2, "0")).join("");
}
function capitalizeFirst(text) {
  if (typeof text !== "string") return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

class StargateAdapter extends BaseAdapter {
  id = "stargate";
  bridgedTokenGroups = [
    ["USDT", "m.USDT"],
    ["USDC", "USDC.e"]
  ];
  initChains() {
    const tokens = this.config;
    const filteredChains = [];
    const filteredTokens = tokens.filter((token) => {
      const chainId = stargateChainKey$1[token.chainKey.toUpperCase()];
      if (!!chainId) {
        const endpointId = token.endpointID;
        if (!endpointId) {
          console.error(
            `Can not find Stargate token ${token.token.symbol} endpointID`
          );
          return false;
        }
        const hasChainConfig = !!this.chainConfigs.find(
          (e) => e.id === chainId
        );
        const isExcludedChain = this.excludedChains.includes(chainId);
        return hasChainConfig && !isExcludedChain;
      } else {
        return false;
      }
    });
    const chainMap = /* @__PURE__ */ new Map();
    filteredTokens.forEach((token) => {
      const chainId = stargateChainKey$1[token.chainKey.toUpperCase()];
      if (!chainMap.get(chainId)) {
        chainMap.set(chainId, {
          chainId,
          chainName: capitalizeFirst(token.chainName)
        });
        filteredChains.push({
          chainId,
          chainName: capitalizeFirst(token.chainName)
        });
      }
    });
    this.chains = filteredChains;
    this.chainMap = chainMap;
  }
  initTokens() {
    const tokens = this.config;
    const tokenMap = /* @__PURE__ */ new Map();
    const symbolMap = /* @__PURE__ */ new Map();
    const filteredTokens = tokens.filter((token, index) => {
      const chainId = stargateChainKey$1[token.chainKey.toUpperCase()];
      if (chainId) {
        const isExcludedToken = this.isExcludedToken({
          chainId,
          tokenSymbol: token.token.symbol?.toUpperCase(),
          tokenAddress: token.address
        });
        const anotherTokenIndex = tokens.findIndex(
          (e, eIndex) => e.token.symbol.toUpperCase() === token.token.symbol.toUpperCase() && e.chainKey === token.chainKey && eIndex !== index
        );
        const isDuplicatedToken = anotherTokenIndex > -1 && anotherTokenIndex !== index;
        if (isDuplicatedToken) {
          console.log(
            `Duplicate Stargate token ${token.token.symbol} symbol in ${token.chainName}`
          );
        }
        return !isExcludedToken && !isDuplicatedToken;
      }
      return false;
    });
    if (filteredTokens.length > 0) {
      filteredTokens.forEach((token) => {
        const chainId = stargateChainKey$1[token.chainKey.toUpperCase()];
        if (this.chainMap.has(chainId) && chainId) {
          if (!symbolMap.get(chainId)) {
            symbolMap.set(chainId, /* @__PURE__ */ new Map());
          }
          const tokenSymbol = token.token.symbol?.toUpperCase();
          if (!symbolMap.get(chainId)?.get(tokenSymbol)) {
            symbolMap.get(chainId)?.set(tokenSymbol, []);
          }
          symbolMap.get(chainId)?.get(tokenSymbol)?.push(token);
          const existTokens = tokenMap.get(chainId);
          if (existTokens && existTokens?.length > 0) {
            tokenMap.set(chainId, existTokens?.concat(token));
          } else {
            tokenMap.set(chainId, [token]);
          }
        }
      });
    }
    this.tokenMap = tokenMap;
    this.symbolMap = symbolMap;
  }
  initTransferMap() {
    const transferMap = /* @__PURE__ */ new Map();
    this.chains.forEach((fromChain) => {
      this.chains.forEach((toChain) => {
        if (fromChain.chainId !== toChain.chainId && fromChain.network === toChain.network) {
          const fromTokens = this.tokenMap.get(fromChain.chainId) ?? [];
          const tokenPairsMap = /* @__PURE__ */ new Map();
          fromTokens.forEach((fromToken) => {
            const toTokens = this.getToTokensForPair({
              fromChainId: fromChain.chainId,
              toChainId: toChain.chainId,
              fromTokenSymbol: fromToken.token.symbol?.toUpperCase()
            });
            if (toTokens?.length) {
              const tokenPairs = [];
              toTokens.forEach((toToken) => {
                tokenPairs.push({
                  fromChainId: fromChain.chainId,
                  toChainId: toChain.chainId,
                  fromToken,
                  toToken,
                  fromTokenAddress: fromToken.token.address,
                  toTokenAddress: toToken.token.address
                });
              });
              tokenPairsMap.set(
                fromToken.token.address?.toLowerCase(),
                tokenPairs
              );
            }
          });
          if (tokenPairsMap.size > 0) {
            if (!transferMap.has(fromChain.chainId)) {
              transferMap.set(
                fromChain.chainId,
                /* @__PURE__ */ new Map()
              );
            }
            transferMap.get(fromChain.chainId)?.set(toChain.chainId, tokenPairsMap);
          }
        }
      });
    });
    this.transferMap = transferMap;
  }
  getChainId(chain) {
    return chain.chainId;
  }
  getTokenBaseInfo({
    chainId,
    token
  }) {
    return {
      name: token.token.symbol,
      symbol: token.token.symbol,
      address: token.token.address,
      decimals: token.token.decimals,
      ...this.getTokenDisplaySymbolAndIcon({
        chainId,
        tokenAddress: token.token.address,
        defaultSymbol: token.token.symbol
      })
    };
  }
}

function stargate(params) {
  return {
    id: "stargate",
    ...params
  };
}

const STARGATE_POOL = [
  {
    inputs: [
      { internalType: "string", name: "_lpTokenName", type: "string" },
      { internalType: "string", name: "_lpTokenSymbol", type: "string" },
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "uint8", name: "_tokenDecimals", type: "uint8" },
      { internalType: "uint8", name: "_sharedDecimals", type: "uint8" },
      { internalType: "address", name: "_endpoint", type: "address" },
      { internalType: "address", name: "_owner", type: "address" }
    ],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  { inputs: [], name: "InvalidLocalDecimals", type: "error" },
  { inputs: [], name: "Path_AlreadyHasCredit", type: "error" },
  { inputs: [], name: "Path_InsufficientCredit", type: "error" },
  { inputs: [], name: "Path_UnlimitedCredit", type: "error" },
  {
    inputs: [
      { internalType: "uint256", name: "amountLD", type: "uint256" },
      { internalType: "uint256", name: "minAmountLD", type: "uint256" }
    ],
    name: "SlippageExceeded",
    type: "error"
  },
  { inputs: [], name: "StargatePoolMigratable_BurnAmountExceedsBalance", type: "error" },
  { inputs: [], name: "Stargate_InsufficientFare", type: "error" },
  { inputs: [], name: "Stargate_InvalidAmount", type: "error" },
  { inputs: [], name: "Stargate_InvalidPath", type: "error" },
  { inputs: [], name: "Stargate_InvalidTokenDecimals", type: "error" },
  { inputs: [], name: "Stargate_LzTokenUnavailable", type: "error" },
  { inputs: [], name: "Stargate_OnlyTaxi", type: "error" },
  { inputs: [], name: "Stargate_OutflowFailed", type: "error" },
  { inputs: [], name: "Stargate_Paused", type: "error" },
  { inputs: [], name: "Stargate_RecoverTokenUnsupported", type: "error" },
  { inputs: [], name: "Stargate_ReentrantCall", type: "error" },
  { inputs: [], name: "Stargate_SlippageTooHigh", type: "error" },
  { inputs: [], name: "Stargate_Unauthorized", type: "error" },
  { inputs: [], name: "Stargate_UnreceivedTokenNotFound", type: "error" },
  { inputs: [], name: "Transfer_ApproveFailed", type: "error" },
  { inputs: [], name: "Transfer_TransferFailed", type: "error" },
  {
    anonymous: false,
    inputs: [
      {
        components: [
          { internalType: "address", name: "feeLib", type: "address" },
          { internalType: "address", name: "planner", type: "address" },
          { internalType: "address", name: "treasurer", type: "address" },
          { internalType: "address", name: "tokenMessaging", type: "address" },
          { internalType: "address", name: "creditMessaging", type: "address" },
          { internalType: "address", name: "lzToken", type: "address" }
        ],
        indexed: false,
        internalType: "struct StargateBase.AddressConfig",
        name: "config",
        type: "tuple"
      }
    ],
    name: "AddressConfigSet",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "uint32", name: "srcEid", type: "uint32" },
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "uint64", name: "amount", type: "uint64" }
        ],
        indexed: false,
        internalType: "struct Credit[]",
        name: "credits",
        type: "tuple[]"
      }
    ],
    name: "CreditsReceived",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "uint32", name: "dstEid", type: "uint32" },
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "uint64", name: "amount", type: "uint64" }
        ],
        indexed: false,
        internalType: "struct Credit[]",
        name: "credits",
        type: "tuple[]"
      }
    ],
    name: "CreditsSent",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "payer", type: "address" },
      { indexed: true, internalType: "address", name: "receiver", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountLD", type: "uint256" }
    ],
    name: "Deposited",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "uint32", name: "dstEid", type: "uint32" },
      { indexed: false, internalType: "bool", name: "oft", type: "bool" }
    ],
    name: "OFTPathSet",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "bytes32", name: "guid", type: "bytes32" },
      { indexed: false, internalType: "uint32", name: "srcEid", type: "uint32" },
      { indexed: true, internalType: "address", name: "toAddress", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountReceivedLD", type: "uint256" }
    ],
    name: "OFTReceived",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "bytes32", name: "guid", type: "bytes32" },
      { indexed: false, internalType: "uint32", name: "dstEid", type: "uint32" },
      { indexed: true, internalType: "address", name: "fromAddress", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountSentLD", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "amountReceivedLD", type: "uint256" }
    ],
    name: "OFTSent",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "previousOwner", type: "address" },
      { indexed: true, internalType: "address", name: "newOwner", type: "address" }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "uint64", name: "amountSD", type: "uint64" }],
    name: "Path_CreditBurned",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "bool", name: "paused", type: "bool" }],
    name: "PauseSet",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "uint256", name: "amount", type: "uint256" }],
    name: "PlannerFeeWithdrawn",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "payer", type: "address" },
      { indexed: true, internalType: "address", name: "receiver", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountLD", type: "uint256" }
    ],
    name: "Redeemed",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: "uint64", name: "amountSD", type: "uint64" }],
    name: "TreasuryFeeAdded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "address", name: "to", type: "address" },
      { indexed: false, internalType: "uint64", name: "amountSD", type: "uint64" }
    ],
    name: "TreasuryFeeWithdrawn",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bytes32", name: "guid", type: "bytes32" },
      { indexed: false, internalType: "uint8", name: "index", type: "uint8" },
      { indexed: false, internalType: "uint32", name: "srcEid", type: "uint32" },
      { indexed: false, internalType: "address", name: "receiver", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountLD", type: "uint256" },
      { indexed: false, internalType: "bytes", name: "composeMsg", type: "bytes" }
    ],
    name: "UnreceivedTokenCached",
    type: "event"
  },
  {
    inputs: [{ internalType: "uint256", name: "_amountLD", type: "uint256" }],
    name: "addTreasuryFee",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_burnAdmin", type: "address" },
      { internalType: "uint64", name: "_burnAllowanceSD", type: "uint64" }
    ],
    name: "allowBurn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "approvalRequired",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "pure",
    type: "function"
  },
  {
    inputs: [],
    name: "burnAdmin",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "burnAllowanceSD",
    outputs: [{ internalType: "uint64", name: "", type: "uint64" }],
    stateMutability: "view",
    type: "function"
  },
  { inputs: [], name: "burnLocked", outputs: [], stateMutability: "nonpayable", type: "function" },
  {
    inputs: [],
    name: "deficitOffset",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_receiver", type: "address" },
      { internalType: "uint256", name: "_amountLD", type: "uint256" }
    ],
    name: "deposit",
    outputs: [{ internalType: "uint256", name: "amountLD", type: "uint256" }],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [],
    name: "endpoint",
    outputs: [{ internalType: "contract ILayerZeroEndpointV2", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "getAddressConfig",
    outputs: [
      {
        components: [
          { internalType: "address", name: "feeLib", type: "address" },
          { internalType: "address", name: "planner", type: "address" },
          { internalType: "address", name: "treasurer", type: "address" },
          { internalType: "address", name: "tokenMessaging", type: "address" },
          { internalType: "address", name: "creditMessaging", type: "address" },
          { internalType: "address", name: "lzToken", type: "address" }
        ],
        internalType: "struct StargateBase.AddressConfig",
        name: "",
        type: "tuple"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "getTransferGasLimit",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "localEid",
    outputs: [{ internalType: "uint32", name: "", type: "uint32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "lpToken",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "oftVersion",
    outputs: [
      { internalType: "bytes4", name: "interfaceId", type: "bytes4" },
      { internalType: "uint64", name: "version", type: "uint64" }
    ],
    stateMutability: "pure",
    type: "function"
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint32", name: "eid", type: "uint32" }],
    name: "paths",
    outputs: [{ internalType: "uint64", name: "credit", type: "uint64" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "plannerFee",
    outputs: [{ internalType: "uint256", name: "available", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "poolBalance",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" }
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple"
      }
    ],
    name: "quoteOFT",
    outputs: [
      {
        components: [
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "uint256", name: "maxAmountLD", type: "uint256" }
        ],
        internalType: "struct OFTLimit",
        name: "limit",
        type: "tuple"
      },
      {
        components: [
          { internalType: "int256", name: "feeAmountLD", type: "int256" },
          { internalType: "string", name: "description", type: "string" }
        ],
        internalType: "struct OFTFeeDetail[]",
        name: "oftFeeDetails",
        type: "tuple[]"
      },
      {
        components: [
          { internalType: "uint256", name: "amountSentLD", type: "uint256" },
          { internalType: "uint256", name: "amountReceivedLD", type: "uint256" }
        ],
        internalType: "struct OFTReceipt",
        name: "receipt",
        type: "tuple"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" }
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple"
      },
      { internalType: "bool", name: "_payInLzToken", type: "bool" }
    ],
    name: "quoteRedeemSend",
    outputs: [
      {
        components: [
          { internalType: "uint256", name: "nativeFee", type: "uint256" },
          { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
        ],
        internalType: "struct MessagingFee",
        name: "fee",
        type: "tuple"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" }
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple"
      },
      { internalType: "bool", name: "_payInLzToken", type: "bool" }
    ],
    name: "quoteSend",
    outputs: [
      {
        components: [
          { internalType: "uint256", name: "nativeFee", type: "uint256" },
          { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
        ],
        internalType: "struct MessagingFee",
        name: "fee",
        type: "tuple"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint32", name: "_srcEid", type: "uint32" },
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "uint64", name: "amount", type: "uint64" }
        ],
        internalType: "struct Credit[]",
        name: "_credits",
        type: "tuple[]"
      }
    ],
    name: "receiveCredits",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "bytes32", name: "sender", type: "bytes32" },
          { internalType: "uint64", name: "nonce", type: "uint64" }
        ],
        internalType: "struct Origin",
        name: "_origin",
        type: "tuple"
      },
      { internalType: "bytes32", name: "_guid", type: "bytes32" },
      { internalType: "uint8", name: "_seatNumber", type: "uint8" },
      { internalType: "address", name: "_receiver", type: "address" },
      { internalType: "uint64", name: "_amountSD", type: "uint64" }
    ],
    name: "receiveTokenBus",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "bytes32", name: "sender", type: "bytes32" },
          { internalType: "uint64", name: "nonce", type: "uint64" }
        ],
        internalType: "struct Origin",
        name: "_origin",
        type: "tuple"
      },
      { internalType: "bytes32", name: "_guid", type: "bytes32" },
      { internalType: "address", name: "_receiver", type: "address" },
      { internalType: "uint64", name: "_amountSD", type: "uint64" },
      { internalType: "bytes", name: "_composeMsg", type: "bytes" }
    ],
    name: "receiveTokenTaxi",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_token", type: "address" },
      { internalType: "address", name: "_to", type: "address" },
      { internalType: "uint256", name: "_amount", type: "uint256" }
    ],
    name: "recoverToken",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint256", name: "_amountLD", type: "uint256" },
      { internalType: "address", name: "_receiver", type: "address" }
    ],
    name: "redeem",
    outputs: [{ internalType: "uint256", name: "amountLD", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" }
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint256", name: "nativeFee", type: "uint256" },
          { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
        ],
        internalType: "struct MessagingFee",
        name: "_fee",
        type: "tuple"
      },
      { internalType: "address", name: "_refundAddress", type: "address" }
    ],
    name: "redeemSend",
    outputs: [
      {
        components: [
          { internalType: "bytes32", name: "guid", type: "bytes32" },
          { internalType: "uint64", name: "nonce", type: "uint64" },
          {
            components: [
              { internalType: "uint256", name: "nativeFee", type: "uint256" },
              { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
            ],
            internalType: "struct MessagingFee",
            name: "fee",
            type: "tuple"
          }
        ],
        internalType: "struct MessagingReceipt",
        name: "msgReceipt",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint256", name: "amountSentLD", type: "uint256" },
          { internalType: "uint256", name: "amountReceivedLD", type: "uint256" }
        ],
        internalType: "struct OFTReceipt",
        name: "oftReceipt",
        type: "tuple"
      }
    ],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "_owner", type: "address" }],
    name: "redeemable",
    outputs: [{ internalType: "uint256", name: "amountLD", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "renounceOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "bytes32", name: "_guid", type: "bytes32" },
      { internalType: "uint8", name: "_index", type: "uint8" },
      { internalType: "uint32", name: "_srcEid", type: "uint32" },
      { internalType: "address", name: "_receiver", type: "address" },
      { internalType: "uint256", name: "_amountLD", type: "uint256" },
      { internalType: "bytes", name: "_composeMsg", type: "bytes" }
    ],
    name: "retryReceiveToken",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" }
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint256", name: "nativeFee", type: "uint256" },
          { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
        ],
        internalType: "struct MessagingFee",
        name: "_fee",
        type: "tuple"
      },
      { internalType: "address", name: "_refundAddress", type: "address" }
    ],
    name: "send",
    outputs: [
      {
        components: [
          { internalType: "bytes32", name: "guid", type: "bytes32" },
          { internalType: "uint64", name: "nonce", type: "uint64" },
          {
            components: [
              { internalType: "uint256", name: "nativeFee", type: "uint256" },
              { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
            ],
            internalType: "struct MessagingFee",
            name: "fee",
            type: "tuple"
          }
        ],
        internalType: "struct MessagingReceipt",
        name: "msgReceipt",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint256", name: "amountSentLD", type: "uint256" },
          { internalType: "uint256", name: "amountReceivedLD", type: "uint256" }
        ],
        internalType: "struct OFTReceipt",
        name: "oftReceipt",
        type: "tuple"
      }
    ],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint32", name: "_dstEid", type: "uint32" },
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "uint64", name: "amount", type: "uint64" },
          { internalType: "uint64", name: "minAmount", type: "uint64" }
        ],
        internalType: "struct TargetCredit[]",
        name: "_credits",
        type: "tuple[]"
      }
    ],
    name: "sendCredits",
    outputs: [
      {
        components: [
          { internalType: "uint32", name: "srcEid", type: "uint32" },
          { internalType: "uint64", name: "amount", type: "uint64" }
        ],
        internalType: "struct Credit[]",
        name: "",
        type: "tuple[]"
      }
    ],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint32", name: "dstEid", type: "uint32" },
          { internalType: "bytes32", name: "to", type: "bytes32" },
          { internalType: "uint256", name: "amountLD", type: "uint256" },
          { internalType: "uint256", name: "minAmountLD", type: "uint256" },
          { internalType: "bytes", name: "extraOptions", type: "bytes" },
          { internalType: "bytes", name: "composeMsg", type: "bytes" },
          { internalType: "bytes", name: "oftCmd", type: "bytes" }
        ],
        internalType: "struct SendParam",
        name: "_sendParam",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint256", name: "nativeFee", type: "uint256" },
          { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
        ],
        internalType: "struct MessagingFee",
        name: "_fee",
        type: "tuple"
      },
      { internalType: "address", name: "_refundAddress", type: "address" }
    ],
    name: "sendToken",
    outputs: [
      {
        components: [
          { internalType: "bytes32", name: "guid", type: "bytes32" },
          { internalType: "uint64", name: "nonce", type: "uint64" },
          {
            components: [
              { internalType: "uint256", name: "nativeFee", type: "uint256" },
              { internalType: "uint256", name: "lzTokenFee", type: "uint256" }
            ],
            internalType: "struct MessagingFee",
            name: "fee",
            type: "tuple"
          }
        ],
        internalType: "struct MessagingReceipt",
        name: "msgReceipt",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint256", name: "amountSentLD", type: "uint256" },
          { internalType: "uint256", name: "amountReceivedLD", type: "uint256" }
        ],
        internalType: "struct OFTReceipt",
        name: "oftReceipt",
        type: "tuple"
      },
      {
        components: [
          { internalType: "uint72", name: "ticketId", type: "uint72" },
          { internalType: "bytes", name: "passengerBytes", type: "bytes" }
        ],
        internalType: "struct Ticket",
        name: "ticket",
        type: "tuple"
      }
    ],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "feeLib", type: "address" },
          { internalType: "address", name: "planner", type: "address" },
          { internalType: "address", name: "treasurer", type: "address" },
          { internalType: "address", name: "tokenMessaging", type: "address" },
          { internalType: "address", name: "creditMessaging", type: "address" },
          { internalType: "address", name: "lzToken", type: "address" }
        ],
        internalType: "struct StargateBase.AddressConfig",
        name: "_config",
        type: "tuple"
      }
    ],
    name: "setAddressConfig",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_deficitOffsetLD", type: "uint256" }],
    name: "setDeficitOffset",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "uint32", name: "_dstEid", type: "uint32" },
      { internalType: "bool", name: "_oft", type: "bool" }
    ],
    name: "setOFTPath",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "bool", name: "_paused", type: "bool" }],
    name: "setPause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "_gasLimit", type: "uint256" }],
    name: "setTransferGasLimit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "sharedDecimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "stargateType",
    outputs: [{ internalType: "enum StargateType", name: "", type: "uint8" }],
    stateMutability: "pure",
    type: "function"
  },
  {
    inputs: [],
    name: "status",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "token",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "address", name: "newOwner", type: "address" }],
    name: "transferOwnership",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "treasuryFee",
    outputs: [{ internalType: "uint64", name: "", type: "uint64" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "tvl",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "bytes32", name: "guid", type: "bytes32" },
      { internalType: "uint8", name: "index", type: "uint8" }
    ],
    name: "unreceivedTokens",
    outputs: [{ internalType: "bytes32", name: "hash", type: "bytes32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "withdrawPlannerFee",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "_to", type: "address" },
      { internalType: "uint64", name: "_amountSD", type: "uint64" }
    ],
    name: "withdrawTreasuryFee",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  }
];

const stargateChainKey = {
  1: "ethereum",
  10: "optimism",
  14: "flare",
  56: "bsc",
  137: "polygon",
  250: "fantom",
  1088: "metis",
  1116: "coredao",
  1329: "sei",
  1625: "gravity",
  2222: "kava",
  5e3: "mantle",
  8217: "klaytn",
  8453: "base",
  8822: "iota",
  42161: "arbitrum",
  43114: "avalanche",
  59144: "linea",
  167e3: "taiko",
  534352: "scroll",
  1313161554: "aurora",
  1380012617: "rarible"
};

function stargateConfig(options) {
  return {
    bridgeType: "stargate",
    timeout: CLIENT_TIME_OUT,
    ...options
  };
}
class Stargate {
  client;
  constructor(config) {
    const { timeout, endpoint } = config;
    this.client = axios.create({
      timeout,
      baseURL: endpoint
    });
  }
  // https://mainnet.stargate-api.com/v1/swagger
  async getBusQueueTime({
    fromEndpointId,
    toEndpointId
  }) {
    return (await this.client.get(
      `${fromEndpointId}/${toEndpointId}`
    )).data;
  }
  // https://stargateprotocol.gitbook.io/stargate/v/v2-developer-docs/integrate-with-stargate/estimating-fees#quoteoft
  async getQuoteOFT({
    publicClient,
    bridgeAddress,
    endPointId,
    receiver,
    amount
  }) {
    const sendParams = {
      dstEid: endPointId,
      to: pad(receiver, { size: 32 }),
      amountLD: amount,
      minAmountLD: amount,
      extraOptions: "0x",
      composeMsg: "0x",
      oftCmd: "0x01"
      // '0x01' for bus, '' for taxi
    };
    try {
      const quoteOFTResponse = await publicClient?.readContract({
        address: bridgeAddress,
        abi: STARGATE_POOL,
        functionName: "quoteOFT",
        args: [sendParams]
      });
      return quoteOFTResponse;
    } catch (error) {
      throw new Error(`Failed to get quote OFT: ${error}`);
    }
  }
  // https://stargateprotocol.gitbook.io/stargate/v/v2-developer-docs/integrate-with-stargate/estimating-fees#quotesend
  async getQuoteSend({
    publicClient,
    bridgeAddress,
    endPointId,
    receiver,
    amount,
    minAmount
  }) {
    const sendParams = {
      dstEid: endPointId,
      to: pad(receiver, { size: 32 }),
      amountLD: amount,
      minAmountLD: minAmount,
      extraOptions: "0x",
      composeMsg: "0x",
      oftCmd: "0x01"
      // '0x01' for bus, '' for taxi
    };
    try {
      const quoteSendResponse = await publicClient?.readContract({
        address: bridgeAddress,
        abi: STARGATE_POOL,
        functionName: "quoteSend",
        args: [sendParams, false]
        // false for not paying lzToken
      });
      return quoteSendResponse;
    } catch (error) {
      throw new Error(`Failed to get quote send: ${error}`);
    }
  }
  /**
   * Send token through Stargate bridge
   * @param {WalletClient} walletClient Wallet client
   * @param {PublicClient} publicClient Public client
   * @param {Address} bridgeAddress Bridge address
   * @param {Address} tokenAddress ERC-20 token address
   * @param {Number} endPointId Stargate end point ID
   * @param {Address} receiver Receiver address
   * @param {BigInt} amount Send amount
   * @returns {Hash} transaction hash
   */
  async sendToken({
    walletClient,
    publicClient,
    bridgeAddress,
    tokenAddress,
    endPointId,
    receiver,
    amount
  }) {
    const sendParams = {
      dstEid: endPointId,
      to: pad(receiver, { size: 32 }),
      amountLD: amount,
      minAmountLD: amount,
      extraOptions: "0x",
      composeMsg: "0x",
      oftCmd: "0x01"
      // '0x01' for bus, '' for taxi
    };
    try {
      const quoteOFTResponse = await this.getQuoteOFT({
        publicClient,
        bridgeAddress,
        endPointId,
        receiver,
        amount
      });
      if (quoteOFTResponse?.[2].amountReceivedLD) {
        sendParams.minAmountLD = BigInt(quoteOFTResponse[2].amountReceivedLD);
      }
      const quoteSendResponse = await this.getQuoteSend({
        publicClient,
        bridgeAddress,
        endPointId,
        receiver,
        amount,
        minAmount: sendParams.minAmountLD
      });
      let nativeFee = quoteSendResponse.nativeFee;
      if (tokenAddress === "0x0000000000000000000000000000000000000000") {
        nativeFee += sendParams.amountLD;
      }
      const sendTokenArgs = {
        address: bridgeAddress,
        abi: STARGATE_POOL,
        functionName: "sendToken",
        args: [sendParams, quoteSendResponse, receiver],
        value: nativeFee,
        account: receiver
      };
      const hash = await walletClient?.writeContract({
        ...sendTokenArgs
      });
      return hash;
    } catch (error) {
      throw new Error(`Failed to send token: ${error}`);
    }
  }
  /**
   * Validate token information
   * @param {fromBridgeAddress} from chain bridge contract address
   * @param {fromTokenAddress}  from token address
   * @param {fromTokenSymbol}   from token symbol
   * @param {fromChainId}       from chain id
   * @param {toTokenAddress}    to token address
   * @param {toTokenSymbol}     to token symbol
   * @param {toChainId}         to chain id
   * @param {toBridgeAddress}   to chain bridge contract address
   * @param {amount}            send token amount
   * @param {toPublicClient}    to chain public client
   * @param {fromPublicClient}  from chain public client
   * @param {dstEndpointId}     Stargate destination endpoint id
   * @param {stargateEndpoint}  Stargate API endpoint
   * @returns {boolean}         is token information valid or not
   */
  async validateStargateToken({
    fromBridgeAddress,
    fromTokenAddress,
    fromTokenDecimals,
    fromTokenSymbol,
    fromChainId,
    toTokenAddress,
    toTokenSymbol,
    toTokenDecimals,
    toChainId,
    toBridgeAddress,
    amount,
    toPublicClient,
    fromPublicClient,
    dstEndpointId,
    stargateEndpoint
  }) {
    try {
      if (amount <= 0) {
        console.log("Invalid amount", amount);
        return false;
      }
      if (!fromChainId || !fromTokenAddress || !fromTokenSymbol || !fromTokenDecimals || !fromBridgeAddress || !toBridgeAddress || !toTokenAddress || !toTokenSymbol || !toTokenDecimals || !toChainId || !amount || !toPublicClient || !fromPublicClient || !dstEndpointId) {
        console.log("Missing Stargate params");
        console.log("-- from ChainId", fromChainId);
        console.log("-- from TokenAddress", fromTokenAddress);
        console.log("-- from TokenSymbol", fromTokenSymbol);
        console.log("-- from TokenDecimals", fromTokenDecimals);
        console.log("-- from bridgeAddress", fromBridgeAddress);
        console.log("-- to ChainId", toChainId);
        console.log("-- to TokenAddress", toTokenAddress);
        console.log("-- to bridgeAddress", toBridgeAddress);
        console.log("-- to TokenSymbol", toTokenSymbol);
        console.log("-- to TokenDecimals", toTokenDecimals);
        console.log("-- amount", amount);
        console.log("-- to PublicClient", toPublicClient);
        console.log("-- from PublicClient", fromPublicClient);
        console.log("-- dstEndpointId", dstEndpointId);
        console.log("-- stargateEndpoint", stargateEndpoint);
        return false;
      }
      if (!isEvmAddress(fromTokenAddress) || !isEvmAddress(fromBridgeAddress) || !isEvmAddress(toBridgeAddress) || !isEvmAddress(toTokenAddress)) {
        console.log(
          "Invalid Stargate Evm Address",
          fromTokenAddress,
          fromBridgeAddress,
          toBridgeAddress,
          toTokenAddress
        );
        return false;
      }
      const fromChainKey = stargateChainKey[fromChainId] ?? "";
      const toChainKey = stargateChainKey[toChainId] ?? "";
      if (!fromChainKey || !toChainKey) {
        console.log("Failed to get chain key");
        console.log("From chain key", fromChainKey, fromChainId);
        console.log("To chain key", toChainKey, toChainId);
        return false;
      }
      const fromApiTokenAddr = await fromPublicClient.readContract({
        address: fromBridgeAddress,
        abi: STARGATE_POOL,
        functionName: "token"
      });
      if (fromApiTokenAddr.toLowerCase() !== fromTokenAddress.toLowerCase()) {
        console.log("Stargate from token address not matched");
        console.log("fromTokenAddress", fromTokenAddress);
        console.log("from token address in API", fromApiTokenAddr);
        return false;
      }
      const dstId = await toPublicClient.readContract({
        address: toBridgeAddress,
        abi: STARGATE_POOL,
        functionName: "localEid"
      });
      if (dstId !== dstEndpointId) {
        console.log("Stargate endpoint Id not matched", dstId, dstEndpointId);
        return false;
      }
      const toApiTokenAddr = await toPublicClient.readContract({
        address: toBridgeAddress,
        abi: STARGATE_POOL,
        functionName: "token"
      });
      if (toApiTokenAddr.toLowerCase() !== toTokenAddress.toLowerCase()) {
        console.log(
          "Stargate to token address not matched",
          toApiTokenAddr,
          toTokenAddress
        );
        return false;
      }
      const { data: stargateConfig2 } = await axios.get(`${stargateEndpoint}`, { timeout: VALIDATION_API_TIMEOUT });
      if (!stargateConfig2) {
        console.log("Failed to get Stargate API config");
        return false;
      }
      const fromTokenInfo = stargateConfig2.data?.v2?.filter((fromToken) => {
        return fromToken.chainKey.toLowerCase() === fromChainKey.toLowerCase() && fromToken.address.toLowerCase() === fromBridgeAddress.toLowerCase() && // bridge contract address
        fromToken.token.decimals === fromTokenDecimals && fromToken.token.symbol.toLowerCase() === fromTokenSymbol.toLowerCase() && fromToken.token.address.toLowerCase() === fromTokenAddress.toLowerCase();
      });
      const toTokenInfo = stargateConfig2.data?.v2?.filter((toToken) => {
        return toToken.chainKey.toLowerCase() === toChainKey.toLowerCase() && toToken.address.toLowerCase() === toBridgeAddress.toLowerCase() && toToken.token.symbol.toLowerCase() === toTokenSymbol.toLowerCase() && toToken.token.decimals === toTokenDecimals && toToken.token.address.toLowerCase() === toTokenAddress.toLowerCase();
      });
      if (!!fromTokenInfo && fromTokenInfo.length > 0 && !!toTokenInfo && toTokenInfo.length > 0) {
        console.log("Stargate token info matched");
        console.log("fromTokenInfo", fromTokenInfo);
        console.log("toTokenInfo", toTokenInfo);
        return true;
      }
      console.log("Could not find Stargate token info");
      console.log("-- from ChainId", fromChainId);
      console.log("-- from TokenAddress", fromTokenAddress);
      console.log("-- from TokenSymbol", fromTokenSymbol);
      console.log("-- from bridgeAddress", fromBridgeAddress);
      console.log("-- to ChainId", toChainId);
      console.log("-- to TokenAddress", toTokenAddress);
      console.log("-- to TokenSymbol", toTokenSymbol);
      console.log("-- to PublicClient", toPublicClient);
      console.log("-- to bridgeAddress", toBridgeAddress);
      console.log("-- amount", amount);
      console.log("-- dstEndpointId", dstEndpointId);
      return false;
    } catch (error) {
      console.log("Stargate token validation error", error);
      return false;
    }
  }
}

class MesonAdapter extends BaseAdapter {
  id = "meson";
  bridgedTokenGroups = [];
  initChains() {
    const chains = this.config;
    const filteredChains = chains.filter((chain) => {
      const hasChainConfig = this.chainConfigs.find(
        (e) => e.id === this.getChainId(chain)
      );
      const isExcludedChain = this.excludedChains.includes(
        this.getChainId(chain)
      );
      const hasToken = chain.tokens?.length > 0;
      return hasChainConfig && !isExcludedChain && hasToken;
    });
    const chainMap = /* @__PURE__ */ new Map();
    filteredChains.forEach((chain) => {
      const chainId = this.getChainId(chain);
      if (!!Number(chainId)) {
        chainMap.set(chainId, chain);
      }
    });
    this.chains = filteredChains;
    this.chainMap = chainMap;
  }
  initTokens() {
    const chains = this.config;
    const tokenMap = /* @__PURE__ */ new Map();
    const symbolMap = /* @__PURE__ */ new Map();
    chains.forEach((chain) => {
      const chainId = this.getChainId(chain);
      const filteredTokens = chain.tokens.filter((token, tokenIndex) => {
        const tokenAddress = token.addr ?? EVM_NATIVE_TOKEN_ADDRESS;
        const isExcludedToken = this.isExcludedToken({
          chainId,
          tokenSymbol: token?.symbol?.toUpperCase(),
          tokenAddress
        });
        const anotherTokenIndex = chain.tokens.findIndex(
          (e, eIndex) => e.symbol.toUpperCase() === token.symbol.toUpperCase() && eIndex !== tokenIndex
        );
        const isDuplicatedToken = anotherTokenIndex > -1 && anotherTokenIndex !== tokenIndex;
        if (isDuplicatedToken) {
          console.log(
            `Duplicate Meson token ${token.symbol} symbol in ${chain.name}`
          );
        }
        return !isExcludedToken && !isDuplicatedToken && !isNativeToken(tokenAddress);
      });
      if (filteredTokens.length > 0 && this.chainMap.has(chainId)) {
        symbolMap.set(chainId, /* @__PURE__ */ new Map());
        filteredTokens.forEach((token) => {
          const tokenSymbol = token.symbol?.toUpperCase();
          if (!symbolMap.get(chainId)?.get(tokenSymbol)) {
            symbolMap.get(chainId)?.set(tokenSymbol, []);
          }
          symbolMap.get(chainId)?.get(tokenSymbol)?.push(token);
        });
        tokenMap.set(chainId, filteredTokens);
      }
    });
    this.tokenMap = tokenMap;
    this.symbolMap = symbolMap;
  }
  initTransferMap() {
    const transferMap = /* @__PURE__ */ new Map();
    this.chains.forEach((fromChain) => {
      this.chains.forEach((toChain) => {
        if (fromChain?.chainId !== toChain?.chainId) {
          const fromChainId = this.getChainId(fromChain);
          const toChainId = this.getChainId(toChain);
          const fromTokens = this.tokenMap.get(fromChainId) ?? [];
          const tokenPairsMap = /* @__PURE__ */ new Map();
          fromTokens.forEach((fromToken) => {
            const toTokens = this.getToTokensForPair({
              fromChainId,
              toChainId,
              fromTokenSymbol: fromToken.symbol?.toUpperCase()
            });
            if (toTokens?.length) {
              const tokenPairs = [];
              toTokens.forEach((toToken) => {
                tokenPairs.push({
                  fromChainId,
                  toChainId,
                  fromToken,
                  toToken,
                  fromTokenAddress: fromToken.addr,
                  toTokenAddress: toToken.addr
                });
              });
              tokenPairsMap.set(fromToken.addr?.toLowerCase(), tokenPairs);
            }
          });
          if (tokenPairsMap.size > 0) {
            if (!transferMap.has(fromChainId)) {
              transferMap.set(
                fromChainId,
                /* @__PURE__ */ new Map()
              );
            }
            transferMap.get(fromChainId)?.set(toChainId, tokenPairsMap);
          }
        }
      });
    });
    this.transferMap = transferMap;
  }
  getChainId(chain) {
    return chain.chainId === "tron" ? TRON_CHAIN_ID : Number(chain.chainId);
  }
  getTokenBaseInfo({
    chainId,
    token
  }) {
    const tokenAddress = token.addr ?? EVM_NATIVE_TOKEN_ADDRESS;
    return {
      name: token.name?.toUpperCase(),
      // TODO
      symbol: token.symbol.toUpperCase(),
      address: tokenAddress,
      decimals: token.decimals,
      ...this.getTokenDisplaySymbolAndIcon({
        chainId,
        tokenAddress,
        defaultSymbol: token.symbol
      })
    };
  }
}

function meson(params) {
  return {
    id: "meson",
    ...params
  };
}

class Meson {
  client;
  constructor(config) {
    const { timeout, endpoint } = config;
    this.client = axios.create({
      timeout,
      baseURL: endpoint
    });
  }
  async getEstimatedFees({
    fromToken,
    toToken,
    amount,
    fromAddr
  }) {
    try {
      const { data: priceResponse } = await this.client.post("/price", {
        from: fromToken,
        to: toToken,
        amount,
        fromAddress: fromAddr,
        fromContract: false
      });
      return priceResponse;
    } catch (error) {
      if (error?.response.data) {
        console.log("Meson fee error", error?.response.data);
        return error?.response?.data;
      } else {
        throw new Error(`Failed to get Meson fees ${error}`);
      }
    }
  }
  async sendToken({
    fromAddress,
    recipient,
    signature,
    encodedData
  }) {
    try {
      const { data: swapResponse } = await this.client.post(
        `/swap/${encodedData}`,
        {
          fromAddress,
          recipient,
          signature
        }
      );
      return swapResponse;
    } catch (error) {
      if (error?.response.data) {
        console.log("Meson send token error", error?.response.data);
        return error?.response?.data;
      } else {
        throw new Error(`Failed to send transaction ${error}`);
      }
    }
  }
  async getUnsignedMessage({
    fromToken,
    toToken,
    amount,
    fromAddress,
    recipient
  }) {
    try {
      const { data: swapResponse } = await this.client.post("/swap", {
        from: fromToken,
        to: toToken,
        amount,
        fromAddress,
        recipient,
        dataToContract: ""
      });
      return swapResponse;
    } catch (error) {
      if (error?.response.data) {
        console.log("Meson get unsigned message error", error?.response.data);
        return error?.response?.data;
      } else {
        throw new Error(`Failed to get unsigned message ${error}`);
      }
    }
  }
  validateMesonToken = async ({
    fromChainId,
    fromChainType,
    fromTokenSymbol,
    fromTokenAddress,
    fromTokenDecimals,
    toTokenAddress,
    toChainId,
    toChainType,
    toTokenSymbol,
    toTokenDecimals,
    amount,
    mesonEndpoint
  }) => {
    try {
      if (Number(amount) <= 0) {
        console.log("Invalid token amount");
        return false;
      }
      if (!fromChainId || !fromTokenAddress || !fromTokenSymbol || !fromTokenDecimals || !fromChainType || !toChainId || !toTokenAddress || !toTokenDecimals || !toChainType || !toTokenSymbol || !amount || !mesonEndpoint) {
        console.log("Missing Meson required params");
        console.log("-- fromChainId", fromChainId);
        console.log("-- fromTokenAddress", fromTokenAddress);
        console.log("-- fromTokenSymbol", fromTokenSymbol);
        console.log("-- fromTokenDecimals", fromTokenDecimals);
        console.log("-- fromChainType", fromChainType);
        console.log("-- toChainId", toChainId);
        console.log("-- toTokenAddress", toTokenAddress);
        console.log("-- toTokenDecimals", toTokenDecimals);
        console.log("-- toChainType", toChainType);
        console.log("-- toTokenSymbol", toTokenSymbol);
        console.log("-- amount", amount);
        console.log("-- mesonEndpoint", mesonEndpoint);
        return false;
      }
      if (fromTokenSymbol.toLowerCase() !== toTokenSymbol.toLowerCase()) {
        console.log("Invalid token symbol", fromTokenSymbol, toTokenSymbol);
        return false;
      }
      if (fromChainType === "evm") {
        if (!isEvmAddress(fromTokenAddress)) {
          console.log("Invalid from token address", fromTokenAddress);
          return false;
        }
      } else if (fromChainType === "tron") {
        if (!isTronAddress(fromTokenAddress)) {
          console.log("Invalid from token address", fromTokenAddress);
          return false;
        }
      } else {
        console.log("Invalid from chain type", fromChainType);
        return false;
      }
      if (toChainType === "evm") {
        if (!isEvmAddress(toTokenAddress)) {
          console.log("Invalid to token address", toTokenAddress);
          return false;
        }
      } else if (toChainType === "tron") {
        if (!isTronAddress(toTokenAddress)) {
          console.log("Invalid to token address", toTokenAddress);
          return false;
        }
      } else {
        console.log("Invalid to chain type", toChainType);
        return false;
      }
      const { data: mesonConfig } = await axios.get(`${mesonEndpoint}/limits`, { timeout: VALIDATION_API_TIMEOUT });
      const fromHexNum = fromChainType === "tron" ? "tron" : `0x${fromChainId?.toString(16)}`;
      const toHexNum = toChainType === "tron" ? "tron" : `0x${toChainId?.toString(16)}`;
      const validFromToken = mesonConfig.result.filter((chainInfo) => {
        const fromTokenInfo = chainInfo.tokens.filter(
          (token) => token?.addr?.toLowerCase() === fromTokenAddress.toLowerCase() && token.decimals === fromTokenDecimals && token.id === fromTokenSymbol.toLowerCase() || !token?.addr && fromTokenAddress === "0x0000000000000000000000000000000000000000" && token.decimals === fromTokenDecimals && token.id === fromTokenSymbol.toLowerCase()
        );
        if (!!fromTokenInfo && fromTokenInfo.length > 0) {
          console.log("Meson from token info", fromTokenInfo);
        }
        return chainInfo.chainId === fromHexNum && fromTokenInfo?.length > 0 && !!fromTokenInfo;
      });
      const validToToken = mesonConfig.result.filter((chainInfo) => {
        const toTokenInfo = chainInfo.tokens.filter(
          (token) => token?.addr?.toLowerCase() === toTokenAddress.toLowerCase() && token.decimals === toTokenDecimals && token.id === toTokenSymbol.toLowerCase() || !token?.addr && toTokenAddress === "0x0000000000000000000000000000000000000000" && token.decimals === toTokenDecimals && token.id === toTokenSymbol.toLowerCase()
        );
        if (!!toTokenInfo && toTokenInfo.length > 0) {
          console.log("Meson to token info", toTokenInfo);
        }
        return chainInfo.chainId === toHexNum && toTokenInfo?.length > 0 && !!toTokenInfo;
      });
      if (validToToken?.length > 0 && validFromToken?.length > 0) {
        console.log("Meson token info matched", validToToken, validFromToken);
        return true;
      }
      console.log("Could not find Meson token");
      console.log("-- fromChainId", fromChainId);
      console.log("-- fromTokenAddress", fromTokenAddress);
      console.log("-- fromTokenSymbol", fromTokenSymbol);
      console.log("-- fromChainType", fromChainType);
      console.log("-- toChainId", toChainId);
      console.log("-- toTokenAddress", toTokenAddress);
      console.log("-- toChainType", toChainType);
      console.log("-- toTokenSymbol", toTokenSymbol);
      console.log("-- amount", amount);
      return false;
    } catch (error) {
      console.log("Meson token validation error", error);
      return false;
    }
  };
}

const DISPLAY_TOKEN_SYMBOLS = {
  10: {
    "0x7F5c764cBc14f9669B88837ca1490cCa17c31607": "USDC.e"
  },
  56: {
    "0x2170Ed0880ac9A755fd29B2688956BD959F933F8": "ETH"
  },
  137: {
    "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174": "USDC.e"
  },
  324: {
    "0x1d17CBcF0D6D143135aE902365D2E5e2A16538D4": "USDC",
    "0x3355df6D4c9C3035724Fd0e3914dE96A5a83aaf4": "USDC.e"
  },
  1101: {
    "0x37eAA0eF3549a5Bb7D431be78a3D99BD360d19e5": "USDC.e"
  },
  42161: {
    "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8": "USDC.e"
  },
  43114: {
    "0xA7D7079b0FEaD91F3e65f86E8915Cb59c1a4C664": "USDC.e",
    "0xc7198437980c041c805A1EDcbA50c1Ce5db95118": "USDT.e",
    "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB": "WETH.e"
  }
};

class Aggregator {
  options;
  adapters = [];
  nativeCurrencies = {};
  constructor(options) {
    this.options = {
      ...options
    };
    this.initNativeCurrencies();
    this.initAdapters();
  }
  initNativeCurrencies() {
    this.nativeCurrencies = {};
    this.options.chainConfigs?.forEach((chain) => {
      if (chain.id && chain.nativeCurrency) {
        this.nativeCurrencies[chain.id] = chain.nativeCurrency;
      }
    });
  }
  initAdapters() {
    const { providers, chainSorter, tokenSorter, ...customizedOptions } = this.options;
    const displayTokenSymbols = isEmpty(customizedOptions.displayTokenSymbols) ? DISPLAY_TOKEN_SYMBOLS : customizedOptions.displayTokenSymbols;
    this.adapters = providers.filter((item) => !!item.config).map((item) => {
      const adapterOptions = {
        ...customizedOptions,
        ...item,
        nativeCurrencies: this.nativeCurrencies,
        displayTokenSymbols
      };
      switch (item.id) {
        case "cBridge":
          return new CBridgeAdapter(adapterOptions).init();
        case "deBridge":
          return new DeBridgeAdapter(adapterOptions).init();
        case "layerZero":
          return new LayerZeroAdapter(adapterOptions).init();
        case "meson":
          return new MesonAdapter(adapterOptions).init();
        default:
          return new StargateAdapter(adapterOptions).init();
      }
    });
  }
  getAdapter(id) {
    return this.adapters.find((item) => item.id === id);
  }
  getNativeCurrency(chainId) {
    if (!chainId) return;
    return this.nativeCurrencies?.[chainId];
  }
  getFromChains() {
    const chainMap = /* @__PURE__ */ new Map();
    this.adapters.forEach((adapter) => {
      const fromChains = adapter.getFromChains();
      fromChains.forEach((item) => {
        const chainId = adapter.getChainId(item);
        const baseInfo = adapter.getChainBaseInfo(chainId);
        let bridgeChain = chainMap.get(chainId);
        bridgeChain = {
          ...bridgeChain ?? baseInfo,
          isCompatible: true
        };
        chainMap.set(chainId, bridgeChain);
      });
    });
    const chains = Array.from(chainMap.values());
    return chains.sort((a, b) => {
      return this.options.chainSorter?.(a, b) ?? 0;
    });
  }
  getToChains({ fromChainId }) {
    const chainMap = /* @__PURE__ */ new Map();
    this.adapters.forEach((adapter) => {
      const toChains = adapter.getToChains({
        fromChainId
      });
      toChains.forEach((item) => {
        const chainId = adapter.getChainId(item);
        const baseInfo = adapter.getChainBaseInfo(chainId);
        let bridgeChain = chainMap.get(chainId);
        const isCompatible = bridgeChain?.isCompatible || adapter.isToChainCompatible({
          fromChainId,
          toChainId: chainId
        });
        bridgeChain = {
          ...bridgeChain ?? baseInfo,
          isCompatible
        };
        chainMap.set(chainId, bridgeChain);
      });
    });
    const chains = Array.from(chainMap.values());
    return chains.sort((a, b) => {
      return this.options.chainSorter?.(a, b) ?? 0;
    });
  }
  getTokens({
    fromChainId,
    toChainId
  }) {
    const tokenMap = /* @__PURE__ */ new Map();
    this.adapters.forEach((adapter) => {
      const allTokenPairs = adapter.getAllTokenPairsOnFromChain({
        fromChainId,
        toChainId
      });
      allTokenPairs.forEach((item) => {
        const tokenPair = item[0];
        const baseInfo = adapter.getTokenBaseInfo({
          chainId: fromChainId,
          token: tokenPair.fromToken
          // fromToken
        });
        const addressKey = baseInfo.address.toLowerCase();
        let bridgeToken = tokenMap.get(addressKey);
        const isCompatible = bridgeToken?.isCompatible || adapter.isTokenCompatible({
          fromChainId,
          toChainId,
          tokenAddress: baseInfo.address
        });
        const isPegged = bridgeToken?.isPegged || !!tokenPair.isPegged;
        bridgeToken = {
          ...bridgeToken ?? baseInfo,
          isCompatible,
          isPegged
        };
        tokenMap.set(addressKey, bridgeToken);
      });
    });
    const tokens = Array.from(tokenMap.values());
    return tokens.sort((a, b) => {
      return this.options.tokenSorter?.(a, b) ?? 0;
    });
  }
  getToTokens({
    fromChainId,
    toChainId,
    tokenAddress
  }) {
    const tokenMap = /* @__PURE__ */ new Map();
    this.adapters.forEach((adapter) => {
      const tokenPairs = adapter.getTokenPairs({
        fromChainId,
        toChainId,
        tokenAddress
      });
      if (tokenPairs) {
        tokenPairs.forEach((item) => {
          const baseInfo = adapter.getTokenBaseInfo({
            chainId: fromChainId,
            token: item.toToken
            // toToken
          });
          const addressKey = baseInfo.address.toLowerCase();
          let bridgeToken = tokenMap.get(addressKey);
          const isPegged = bridgeToken?.isPegged || !!item.isPegged;
          bridgeToken = {
            ...bridgeToken ?? baseInfo,
            isCompatible: true,
            isPegged
          };
          tokenMap.set(addressKey, bridgeToken);
        });
      }
    });
    const tokens = Array.from(tokenMap.values());
    return tokens.sort((a, b) => {
      return this.options.tokenSorter?.(a, b) ?? 0;
    });
  }
  getFromChainDetail({
    fromChainId,
    toChainId,
    tokenAddress
  }) {
    let chainDetail;
    this.adapters.forEach((adapter) => {
      const tokenPairs = adapter.getTokenPairs({
        fromChainId,
        toChainId,
        tokenAddress
      });
      if (tokenPairs) {
        const baseInfo = adapter.getChainBaseInfo(fromChainId);
        chainDetail = {
          ...chainDetail ?? baseInfo,
          isCompatible: true,
          [adapter.id]: {
            raw: adapter.getChainById(fromChainId)
          }
        };
      }
    });
    return chainDetail;
  }
  getToChainDetail({
    fromChainId,
    toChainId,
    tokenAddress
  }) {
    let chainDetail;
    this.adapters.forEach((adapter) => {
      const tokenPairs = adapter.getTokenPairs({
        fromChainId,
        toChainId,
        tokenAddress
      });
      if (tokenPairs) {
        const baseInfo = adapter.getChainBaseInfo(toChainId);
        const isCompatible = chainDetail?.isCompatible || adapter.isToChainCompatible({
          fromChainId,
          toChainId
        });
        chainDetail = {
          ...chainDetail ?? baseInfo,
          isCompatible,
          [adapter.id]: {
            raw: adapter.getChainById(toChainId)
          }
        };
      }
    });
    return chainDetail;
  }
  getTokenDetail({
    fromChainId,
    toChainId,
    tokenAddress
  }) {
    let tokenDetail;
    this.adapters.forEach((adapter) => {
      const tokenPair = adapter.getTokenPairs({
        fromChainId,
        toChainId,
        tokenAddress
      })?.[0];
      if (tokenPair) {
        const baseInfo = adapter.getTokenBaseInfo({
          chainId: fromChainId,
          token: tokenPair.fromToken
        });
        const isCompatible = tokenDetail?.isCompatible || adapter.isTokenCompatible({
          fromChainId,
          toChainId,
          tokenAddress
        });
        const isPegged = tokenDetail?.isPegged || !!tokenPair.isPegged;
        tokenDetail = {
          ...tokenDetail ?? baseInfo,
          isPegged,
          isCompatible
        };
        if (adapter.id === "cBridge" && tokenPair.peggedConfig) {
          tokenDetail.cBridge = {
            ...baseInfo,
            peggedConfig: tokenPair.peggedConfig,
            raw: tokenPair.fromToken
          };
        } else {
          tokenDetail = {
            ...tokenDetail,
            [adapter.id]: {
              ...baseInfo,
              raw: tokenPair.fromToken
            }
          };
        }
      }
    });
    return tokenDetail;
  }
  getToTokenDetail({
    fromChainId,
    toChainId,
    fromTokenAddress,
    toTokenAddress
  }) {
    let tokenDetail;
    this.adapters.forEach((adapter) => {
      const tokenPair = adapter.getTokenPair({
        fromChainId,
        toChainId,
        fromTokenAddress,
        toTokenAddress
      });
      if (tokenPair) {
        const baseInfo = adapter.getTokenBaseInfo({
          chainId: toChainId,
          token: tokenPair.toToken
        });
        const isPegged = tokenDetail?.isPegged || !!tokenPair.isPegged;
        tokenDetail = {
          ...tokenDetail ?? baseInfo,
          isPegged,
          isCompatible: true
        };
        if (adapter.id === "cBridge" && tokenPair.peggedConfig) {
          tokenDetail.cBridge = {
            ...baseInfo,
            peggedConfig: tokenPair.peggedConfig,
            raw: tokenPair.toToken
          };
        } else {
          tokenDetail = {
            ...tokenDetail,
            [adapter.id]: {
              ...baseInfo,
              raw: tokenPair.toToken
            }
          };
        }
      }
    });
    return tokenDetail;
  }
}

const assert = (condition, message) => {
  if (!condition) {
    throw new Error(message);
  }
};

function isAndroid() {
  return typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);
}
function isSmallIOS() {
  return typeof navigator !== "undefined" && /iPhone|iPod/.test(navigator.userAgent);
}
function isLargeIOS() {
  return typeof navigator !== "undefined" && (/iPad/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function isIOS() {
  return isSmallIOS() || isLargeIOS();
}
function isMobile() {
  return isAndroid() || isIOS();
}
function isTMA() {
  if (typeof window === "undefined") {
    return false;
  }
  const check = (host) => {
    return typeof host !== "undefined" && "TelegramWebviewProxy" in host && "postEvent" in host.TelegramWebviewProxy && typeof host.TelegramWebviewProxy.postEvent === "function";
  };
  return check(window);
}

class CanonicalBridgeSDK {
  cBridge;
  deBridge;
  stargate;
  layerZero;
  meson;
  options;
  constructor(options) {
    const cBridgeConfig = options.bridgeConfigs.find(
      (item) => item.bridgeType === "cBridge"
    );
    const deBridgeConfig = options.bridgeConfigs.find(
      (item) => item.bridgeType === "deBridge"
    );
    const stargateConfig = options.bridgeConfigs.find(
      (item) => item.bridgeType === "stargate"
    );
    const layerZeroConfig = options.bridgeConfigs.find(
      (item) => item.bridgeType === "layerZero"
    );
    const mesonConfig = options.bridgeConfigs.find(
      (item) => item.bridgeType === "meson"
    );
    if (cBridgeConfig) {
      this.cBridge = new CBridge(cBridgeConfig);
    }
    if (deBridgeConfig) {
      this.deBridge = new DeBridge(deBridgeConfig);
    }
    if (stargateConfig) {
      this.stargate = new Stargate(stargateConfig);
    }
    if (layerZeroConfig) {
      this.layerZero = new LayerZero();
    }
    if (mesonConfig) {
      this.meson = new Meson(mesonConfig);
    }
    this.options = options;
  }
  /**
   * Get sdk options
   * @returns {CanonicalBridgeSDKOptions<BaseBridgeConfig | DeBridgeConfig>} sdk options
   */
  getSDKOptions() {
    return this.options;
  }
  /**
   * Get supported bridges
   * @returns {BridgeType[]} A string list of supported bridges
   */
  getSupportedBridges() {
    return this.options.bridgeConfigs.map((item) => item.bridgeType);
  }
  /**
   * Approve ERC-20 token
   * @param {WalletClient} walletClient Wallet client
   * @param {Address} tokenAddress ERC-20 token address
   * @param {BigInt} amount approve amount
   * @param {Address} address wallet/account address
   * @param {Address} spender spender address
   * @returns {Hash} transaction hash
   */
  async approveToken({
    walletClient,
    tokenAddress,
    amount,
    address,
    spender
  }) {
    try {
      const hash = await walletClient?.writeContract({
        address: tokenAddress,
        abi: ERC20_TOKEN,
        functionName: "approve",
        args: [spender, amount],
        chain: walletClient.chain,
        account: address
      });
      return hash;
    } catch (error) {
      throw new Error(`Failed to approve token:: ${error}`);
    }
  }
  /**
   * Get token allowance
   * @param {PublicClient} publicClient Public client
   * @param {Address} tokenAddress ERC-20 token address
   * @param {Address} owner owner/account address
   * @param {Address} spender spender address
   * @returns {BigInt} token allowance amount
   */
  async getTokenAllowance({
    publicClient,
    tokenAddress,
    owner,
    spender
  }) {
    try {
      const allowance = await publicClient?.readContract({
        abi: ERC20_TOKEN,
        address: tokenAddress,
        functionName: "allowance",
        args: [owner, spender]
      });
      return allowance;
    } catch (error) {
      throw new Error(`Failed to get token allowance: ${error}`);
    }
  }
  /**
   * Get token balance
   * @param {PublicClient} publicClient Public client
   * @param {Address} tokenAddress ERC-20 token address
   * @param {Address} owner owner/account address
   * @returns {BigInt} token balance amount
   */
  async getTokenBalance({
    publicClient,
    tokenAddress,
    owner
  }) {
    try {
      const balance = await publicClient?.readContract({
        abi: ERC20_TOKEN,
        address: tokenAddress,
        functionName: "balanceOf",
        args: [owner]
      });
      return balance;
    } catch (error) {
      throw new Error(`Failed to get token balance: ${error}`);
    }
  }
  /**
   * Load bridge fees and return fee information in the following order
   * [deBridge, cBridge, stargate, layerZero, meson]
   */
  async loadBridgeFees({
    bridgeType,
    fromChainId,
    fromAccount,
    toChainId,
    sendValue,
    fromTokenSymbol,
    publicClient,
    endPointId,
    bridgeAddress,
    isPegged,
    slippage,
    mesonOpts,
    deBridgeOpts,
    toToken
  }) {
    const promiseArr = [];
    if (this.deBridge && deBridgeOpts && bridgeType.includes("deBridge") && toToken?.deBridge) {
      const debridgeFeeAPICall = this.deBridge.getEstimatedFees(deBridgeOpts);
      promiseArr.push(debridgeFeeAPICall);
    } else {
      promiseArr.push(new Promise((reject) => reject(null)));
    }
    if (this.cBridge && slippage && bridgeType.includes("cBridge") && toToken?.cBridge) {
      const cBridgeFeeAPICall = this.cBridge.getEstimatedAmount({
        src_chain_id: fromChainId,
        dst_chain_id: toChainId,
        token_symbol: fromTokenSymbol,
        amt: String(sendValue),
        user_addr: fromAccount,
        slippage_tolerance: slippage,
        is_pegged: isPegged
      });
      promiseArr.push(cBridgeFeeAPICall);
    } else {
      promiseArr.push(new Promise((reject) => reject(null)));
    }
    if (this.stargate && bridgeAddress?.stargate && endPointId?.layerZeroV2 && bridgeType.includes("stargate") && !!publicClient && toToken?.stargate) {
      const stargateFeeAPICall = this.stargate.getQuoteOFT({
        publicClient,
        bridgeAddress: bridgeAddress.stargate,
        endPointId: endPointId.layerZeroV2,
        receiver: fromAccount,
        amount: sendValue
      });
      promiseArr.push(stargateFeeAPICall);
    } else {
      promiseArr.push(new Promise((reject) => reject(null)));
    }
    if (this.layerZero && bridgeAddress?.layerZero && endPointId?.layerZeroV1 && bridgeType.includes("layerZero") && !!publicClient && toToken?.layerZero) {
      const layerZeroFeeAPICall = this.layerZero.getEstimateFee({
        bridgeAddress: bridgeAddress.layerZero,
        amount: sendValue,
        dstEndpoint: endPointId.layerZeroV1,
        userAddress: fromAccount,
        publicClient
      });
      promiseArr.push(layerZeroFeeAPICall);
    } else {
      promiseArr.push(new Promise((reject) => reject(null)));
    }
    if (this.meson && mesonOpts && bridgeType.includes("meson") && toToken?.meson) {
      const mesonFeeAPICall = this.meson.getEstimatedFees(mesonOpts);
      promiseArr.push(mesonFeeAPICall);
    } else {
      promiseArr.push(new Promise((reject) => reject(null)));
    }
    return await Promise.allSettled(promiseArr);
  }
  /**
   * Send token through different bridge mode
   */
  async sendToken({
    bridgeType,
    fromChainId,
    amount,
    userAddress,
    tokenAddress,
    toChainId,
    bridgeAddress,
    walletClient,
    publicClient,
    slippage,
    peggedConfig,
    bridgeEndPointId,
    debridgeOpts,
    cBridgeOpts
  }) {
    if (this.deBridge && bridgeType === "deBridge" && debridgeOpts && debridgeOpts?.data) {
      if (!debridgeOpts?.data) {
        throw new Error("Invalid deBridge data");
      }
      return await this.deBridge.sendToken({
        walletClient,
        bridgeAddress,
        data: debridgeOpts?.data,
        amount,
        address: userAddress
      });
    } else if (this.cBridge && bridgeType === "cBridge" && cBridgeOpts && cBridgeOpts?.isPegged && slippage) {
      const transferType = this.cBridge.getTransferType({
        peggedConfig,
        fromChainId
      });
      const nonce = (/* @__PURE__ */ new Date()).getTime();
      const transferArgs = this.cBridge.getTransferParams({
        amount,
        isPegged: cBridgeOpts.isPegged,
        toChainId,
        tokenAddress,
        address: userAddress,
        maxSlippage: slippage,
        transferType,
        peggedConfig,
        nonce
      });
      return await this.cBridge.sendToken({
        walletClient,
        publicClient,
        bridgeAddress,
        fromChainId,
        address: userAddress,
        isPegged: cBridgeOpts.isPegged,
        peggedConfig,
        args: transferArgs
      });
    } else if (this.stargate && bridgeType === "stargate" && bridgeEndPointId?.layerZeroV2) {
      return await this.stargate.sendToken({
        walletClient,
        publicClient,
        bridgeAddress,
        tokenAddress,
        endPointId: bridgeEndPointId?.layerZeroV2,
        receiver: userAddress,
        amount
      });
    } else if (this.layerZero && bridgeType === "layerZero" && bridgeEndPointId?.layerZeroV1) {
      return await this.layerZero.sendToken({
        userAddress,
        bridgeAddress,
        amount,
        dstEndpoint: bridgeEndPointId?.layerZeroV1,
        publicClient,
        walletClient
      });
    } else {
      throw new Error("Invalid bridge inputs");
    }
  }
}

export { Aggregator, BaseAdapter, CBridge, CBridgeAdapter, CLIENT_TIME_OUT, CanonicalBridgeSDK, DeBridge, DeBridgeAdapter, ERC20_TOKEN, ESTIMATE_AMOUNT_DATA_RELOAD, EVM_NATIVE_TOKEN_ADDRESS, EXPLORER_URL, LayerZero, LayerZeroAdapter, Meson, MesonAdapter, SOLANA_NATIVE_TOKEN_ADDRESS, Stargate, StargateAdapter, TRON_CHAIN_ID, VALIDATION_API_TIMEOUT, assert, cBridge, cBridgeConfig, capitalizeFirst, deBridge, deBridgeConfig, formatNumber, formatSmallNumber, isAndroid, isEmpty, isEvmAddress, isIOS, isLargeIOS, isMobile, isNativeToken, isSameAddress, isSmallIOS, isSolanaAddress, isTMA, isTronAddress, isValidTokenAddress, layerZero, meson, removeAfterDecimals, stargate, stargateConfig, uniqueArr, utf8ToHex };
